import React, { Component } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import { om2confirm } from "omni-campaign-ui";
import { html } from 'lit';

// AG Grid imports
import { AgGridReact } from "ag-grid-react";
import { 
    AllCommunityModule, 
    ModuleRegistry, 
    ClientSideRowModelModule, 
    ValidationModule, 
    NumberEditorModule, 
    DateFilterModule, 
    NumberFilterModule, 
    TextFilterModule, 
    CustomFilterModule, 
    CsvExportModule, 
    RowSelectionModule, 
    DateEditorModule, 
    SelectEditorModule ,
    TooltipModule
} from "ag-grid-community";
import { 
    TreeDataModule, 
    ColumnsToolPanelModule, 
    FiltersToolPanelModule, 
    PivotModule, 
    SetFilterModule, 
    StatusBarModule, 
    ExcelExportModule, 
    LicenseManager, 
    AllEnterpriseModule 
} from "ag-grid-enterprise";

// Shared components and utilities
import { 
    OmniIconComponent, 
    OmniToolbarComponent, 
    OmniTooltipNextComponent, 
    OmniIconMenu, 
    AlertMessagesService, 
    MenuItems, 
    LoaderComponent, 
    MediaOpsService, 
    CustomStatusBar, 
    Utils, 
    CookieService, 
    AGGRID_LICENCE_KEY, 
    placementCreateMenuTypes, 
    env, 
    SyncErrorDialogComponent,
    InlineEditDialogBox,
    OverBudgetWarningModal,
    NavigationStack
} from '../../../../shared';

import { RatesAndFeesService } from '../../../admin/services/ratesAndFees-service';
import { trackEvent } from '../../../../shared/utils/amplitude';

// Placement feature imports (from index.js)
import { 
    PlacementDetailsTableService, 
    PlacementUtils, 
    PlacementDetailsTableSummaryHeader, 
    PlatformDetailDialogComponent,
    PlacementsDialogComponent,
    CreatePlacementOrPackageService,
    EditTemplateDialog
} from '../../';
import PlacementUsageAnalytics from '../../services/placementUsageAnalytics';

// Admin feature imports
import { VendorMappingService } from '../../../admin';

// Local component imports
import { columnDefs, defaultColDef, contextMenuItems } from './placementTableColumnDefs';
import DropdownMenuCellRenderer from './DropdownMenuCellRenderer';
import MovePlacementForm from './MovePlacementForm';
import CampaignStatusWarningModal from './CampaignStatusWarningModal';
import PlacementCache from '../../services/placementCache';

// Register the required modules with the Grid
ModuleRegistry.registerModules([
    TreeDataModule,
    AllCommunityModule,
    ValidationModule,
    NumberEditorModule,
    ColumnsToolPanelModule,
    FiltersToolPanelModule,
    PivotModule,
    SetFilterModule,
    ClientSideRowModelModule,
    DateFilterModule,
    NumberFilterModule,
    TextFilterModule,
    StatusBarModule,
    CustomFilterModule,
    CsvExportModule,
    ExcelExportModule,
    RowSelectionModule,
    DateEditorModule,
    AllEnterpriseModule,
    SelectEditorModule,
    TooltipModule
]);

LicenseManager.setLicenseKey(AGGRID_LICENCE_KEY);

class PlacementTableAgGrid extends Component {
    // Add selectedPlacementForMove as a class property
    selectedPlacementForMove = null;

    taxonomyStrings = {};
    vendorMappingMap = {};
    allTaxonomyData = {};
    placementRowData = null;
    channelDropdownList = [{ title: "Create", submenu: [] }];
    placementTaxonomyMap = {}
    DirectProgrammaticTypes = ['Package', 'Print Offline Package', 'DOOH Package', 'Cinema Offline Package', 'Other Offline Package', 'Unallocated Offline Package', 'National TV Network Package', 'Network Radio Offline Package', 'Spot TV Offline Package', 'Spot Radio Offline Package'];
    DirectProgrammaticMainTypes = ['Direct', 'Social Direct', 'Print', 'OOH', 'Cinema', 'Other', 'Unallocated', 'National TV', 'Network Radio', 'Spot TV', 'Spot Radio'];
    lineItemParentMap = {};
    channelSiteMap = {};
    importExportOptions = ['Import media plan', 'Export media plan', 'Edit template'];
    moErrorObj = {};
    gridContainerRef = null;
    navigationConfirmationMessage = 'Changes you made may not be saved. Are you sure you want to leave this page?';
    originalNavigatorPush = null;
    navigatorRef = null; // To store the navigator object
    isInlineOverride = false;
    static contextType = NavigationContext; // Set contextType to access router's navigator
    inlinePayload = [];
    inlineType = '';
    parameterChildCoulmnName = [];
    defaultTaxonomyData = {};
    rateAndFees = {
        adServer: false,
        adVerification: false
    }
    smartSuggestionsCache = {}; // Cache for lazy-loaded smart suggestions by placement type { 'Direct': { status: 'loading'|'ready', data: {...} } }
    constructor(props) {
        super(props);
        this.gridRef = React.createRef();
        this.setCascadingParentId();
        // this.createTaxonomyColumn();
        this.createParameterColumn();
        
        // Initialize supplierFieldTaxonomy here with proper null checking
        this.supplierFieldTaxonomy = { 
            field_type: 1, 
            name: 'Supplier', 
            alias: 'Supplier', 
            delimiter_value: "_", 
            hidden: false, 
            isExplicitCascading: true, 
            fielditems: (this.props?.state?.campaignObj?.partners || []).map(item => ({ name: item, code: item }))
        };
        this.state = {
            campaignObj: this.props.state?.campaignObj || {},
            placementsFromApi: [],
            placementsWithNestedStructure: [],
            openDropdownId: null,
            columnDefs: columnDefs.map(col => {
                const cellRenderers = {
                    placement_taxonomy: this.placementTaxonomyRenderer,
                    actions: this.actionCellRenderer,
                    mediaopshub_status: this.mediaOpsStatusRenderer,
                    planned_cost: this.netMediaRenderer,
                    rate: this.rateRenderer,
                    unit_amount: this.unitRenderer,
                    ad_server_fee: this.adServingFeeRenderer,
                    ad_verification_fee: this.adVerificationFeeRenderer,
                    total_media: this.totalMediaRenderer,
                    package_type: this.placementTypeRenderer,
                    publisher_budget_in_pmds: this.planningSysytemAmountRenderer,
                };

                if (this.props?.systemParametersData?.data?.isLocal && col.field === 'planned_cost') {
                    return cellRenderers[col.field]
                        ? { ...col, cellRenderer: cellRenderers[col.field], field: 'planned_cost_local' }
                        : col;
                } else if (this.props?.systemParametersData?.data?.isLocal && col.field === 'rate') {
                    return cellRenderers[col.field]
                        ? { ...col, cellRenderer: cellRenderers[col.field], field: 'rate_local' }
                        : col;
                } else if (col.field === 'flight_start') {
                    return {
                        ...col, cellEditorParams: {
                            min: this.props.state?.campaignObj?.['start_date'] || '',
                            max: this.props.state?.campaignObj?.['end_date'] || '',
                        }
                    };
                } else if (col.field === 'flight_end') {
                    return {
                        ...col, cellEditorParams: (params) => ({
                            min: params?.data?.flight_start || '', 
                            max: this.props?.state?.campaignObj?.['end_date'] || '',
                        }),
                    };
                } else if (col.field === 'package_type') {
                    return cellRenderers[col.field]
                        ? { ...col, valueGetter: cellRenderers[col.field] }
                        : col;
                } else if (col.field === 'placement_taxonomy') {
                    return cellRenderers[col.field]
                        ? { ...col, valueGetter: cellRenderers[col.field] }
                        : col;
                } else {
                    return cellRenderers[col.field]
                        ? { ...col, cellRenderer: cellRenderers[col.field] }
                        : col;
                }
            }),
            loading: true,
            statusBar: {
                statusPanels: [
                    {
                        statusPanel: "agAggregationComponent",
                        statusPanelParams: { aggFuncs: ["avg", "sum"], },
                    },
                    {
                        statusPanel: CustomStatusBar, // Add custom status panel
                        align: "left",
                    },
                ],
            },
            displayInlineEditModal: false,
            archiveTab: false,
            selectedItems: [],
            selectedIds: [], // storing ids to check them after reloading the table
            movePlacementModalOpen: false,
            availablePackagesForMove: [],
            selectedPackageId: '',
            isExportInProgress: false, // Added state for export tracking
            editTemplateModalOpen: false,
            showSupplierBudgetWarning: false,
            overBudgetSuppliers: [],
            pendingSendItems: [],
            showCampaignStatusWarning: false,
        }
    }

    /**
     * Handles the `beforeunload` event to display a confirmation dialog
     * when the user attempts to navigate away from the page.
     *
     * @param {Event} event - The beforeunload event object.
     * @returns {string} The confirmation message to display in the dialog.
     */
    handleBeforeUnload = (event) => {
        event.preventDefault();
        event.returnValue = this.navigationConfirmationMessage;
        return this.navigationConfirmationMessage;
    };

    /**
     * Intercepts navigation attempts and prompts the user for confirmation before proceeding.
     * If the user confirms, the original navigation push method is called with the provided arguments.
     *
     * @param {...any} args - Arguments to be passed to the original navigation push method.
     */
    interceptNavigationPush = (...args) => {
        om2confirm(this.navigationConfirmationMessage, {
            callback: () => {
                if (this.originalNavigatorPush) {
                    this.originalNavigatorPush(...args);
                }
            },
            type: "warning",
            title: "Navigation Confirmation"
        });
    };
    
    /**
     * Lifecycle method invoked immediately after the component is mounted.
     * - Resets the spinner count to 0.
     * - Loads vendor mapping data.
     * - Updates all taxonomy data.
     * - Initializes the page.
     * - Stores the navigator from context if available.
     */
    componentDidMount() {
        this.props.setSpinnerCount(0);
        this.updateAllTaxonomyData();
        this.pageInit();

        // Store navigator from context
        if (this.context && this.context.navigator) {
            this.navigatorRef = this.context.navigator;
        }

        // Analytics: page opened
        const campaignId = this.props?.state?.campaignObj?.id || this.props?.params?.campaignId;
        trackEvent(`Placements Page is opened.`, { access_from: 'Placements Page', campaignId });
    }

    /**
     * Attempt to delete the aggregated placement cache for the current campaign
     * in the background. This runs fire-and-forget and logs any errors.
     */
    clearPlacementCacheBackground = () => {
        PlacementCache.deleteDatabase().catch(err => console.error('Error clearing placement cache DB (background initial):', err));
    }

    loadCampaignObj = () => {
        const campaignId = encodeURIComponent(this.state?.campaignObj?.id || this.props?.params?.campaignId);
        MediaOpsService.getCampaignDataById(campaignId, Utils.getRouteParam("clientid"), this.props?.versionOneApi)
            .then((campaignData) => {
                if (!campaignData || !campaignData.length) {
                    return;
                }
                // Merge with the existing campaignObj so derived fields set during page init
                // (e.g. parent campaign's local_currency_name) are not lost on refresh
                const campaignObj = { ...this.state.campaignObj, ...campaignData[0] };
                campaignObj['budget'] = campaignData[0]?.media_attributes?.media_budget?.campaign_budget || '';
                campaignObj['channels'] = campaignData[0]?.media_attributes?.channels || '';
                if (!campaignData[0]?.local_currency_name && this.state?.campaignObj?.local_currency_name) {
                    campaignObj['local_currency_name'] = this.state.campaignObj.local_currency_name;
                }
                this.setState({ campaignObj });
            }).catch((error) => {
                this.props.setSpinnerCount(0);
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Refresh', retryBtnEvent: () => this.loadCampaignObj(), title: 'Unable to load the campaign details API', bodyMsgArr: [] }]);
            });
    }

    /**
     * React lifecycle method that is called after the component updates.
     * 
     * Monitors changes to the `isExportInProgress` state. When an export starts,
     * it adds a `beforeunload` event listener to warn users before leaving the page,
     * and intercepts navigation to prevent accidental navigation away. When the export
     * ends, it removes the event listener and restores the original navigation behavior.
     *
     * @param {Object} prevProps - The previous props of the component.
     * @param {Object} prevState - The previous state of the component.
     */
    componentDidUpdate(prevProps, prevState) {
        // Detect changes in export progress to manage navigation and unload events
        if (prevState.isExportInProgress !== this.state.isExportInProgress) {
            if (this.state.isExportInProgress) {
                window.addEventListener('beforeunload', this.handleBeforeUnload);
                if (this.navigatorRef && !this.originalNavigatorPush) {
                    this.originalNavigatorPush = this.navigatorRef.push;
                    this.navigatorRef.push = this.interceptNavigationPush;
                }
            } else {
                window.removeEventListener('beforeunload', this.handleBeforeUnload);
                if (this.navigatorRef && this.originalNavigatorPush) {
                    this.navigatorRef.push = this.originalNavigatorPush;
                    this.originalNavigatorPush = null;
                }
            }
        }

        // Global search on the SAME campaign page does not remount the component
        // (route key is campaignId), so re-apply the search-bar filter when the
        // targeted placement changes via a new global-search navigation.
        const prevGlobalSearch = prevProps?.state?.navigateFromGlobalSearch && prevProps?.state?.placement;
        const currGlobalSearch = this.props?.state?.navigateFromGlobalSearch && this.props?.state?.placement;
        if (currGlobalSearch && prevProps?.state?.placementId !== this.props?.state?.placementId) {
            this.applyGlobalSearchPlacementFilter();
        }
    }

    /**
     * Applies the AG Grid column filter for the placement targeted by a global-search
     * navigation. Used when navigating to a placement within the already-open campaign,
     * where the component does not remount and the mount-time filter logic is skipped.
     */
    applyGlobalSearchPlacementFilter = async () => {
        const placement = this.props?.state?.placement;
        if (!placement) return;
        const gridApi = this.gridRef?.current?.api;
        if (!gridApi) return;
        await gridApi.setColumnFilterModel('placement_taxonomy', {
            filterType: 'text',
            type: 'contains',
            filter: placement.toString(),
        });
        gridApi.onFilterChanged();
        gridApi.refreshCells({ force: true });
    }


    componentWillUnmount() {
        if (this.state.isExportInProgress) {
            window.removeEventListener('beforeunload', this.handleBeforeUnload);
            // Restore the original navigator push method if it was overridden
            if (this.navigatorRef && this.originalNavigatorPush) {
                this.navigatorRef.push = this.originalNavigatorPush;
                this.originalNavigatorPush = null;
            }
        }
    }


    /**
     * refrest table to show menu on table 
     * @param {number} id placement id
     */
    toggleDropdown = (id) => {
        const newId = this.state.openDropdownId === id ? null : id;
      
        // Immediately update dropdown visibility
        this.setState({ openDropdownId: newId });
      
        // Defer grid refresh slightly more than 0ms (AG Grid needs more time)
        setTimeout(() => {
          this.gridRef?.current?.api?.refreshCells({ force: true });
        }, 50); 
      };

      setCascadingParentId = () =>{
        if (this.props.allTaxonomyData?.defaultTaxonomyData?.fields.length) {
            // this.defaultTaxonomyData = {...this.props.allTaxonomyData.defaultTaxonomyData}; 
            const fields = this.props.allTaxonomyData.defaultTaxonomyData.fields;
            // Clear any previous cascadingParentId assignments
            fields.forEach(f => { delete f.cascadingParentId; });
            fields.forEach(parentField => {
                parentField.fielditems.forEach(fieldItem => {
                    if (Array.isArray(fieldItem.childitems)) {
                        fieldItem.childitems.forEach(childItem => {
                            if (childItem.FieldId) {
                                const childIndex = fields.findIndex(val => val.id == childItem.FieldId);
                                if (childIndex !== -1) {
                                    fields[childIndex].cascadingParentId = parentField.id;
                                }
                            }
                        });
                    }
                });
            });
            this.defaultTaxonomyData = {...this.props.allTaxonomyData.defaultTaxonomyData,fields: fields};
        }
      }

      /**
       * Create parameter column in Placement table
       * all placement parameter under one group column name parameter
       */
      createParameterColumn = () => {
        let column = columnDefs.find(val=> val.headerName == 'PARAMETER')
        let defaultTaxonomyData = this.props.allTaxonomyData?.defaultTaxonomyData;
        if(column?.headerName == 'PARAMETER' && defaultTaxonomyData?.fields?.length === column?.children?.length){
            return;
        }

        column = {
             headerName: 'PARAMETER',
            children: []
        }
        let setparameterChildColumnNames = [];
        // let child = this.state?.childField;
        
        defaultTaxonomyData && defaultTaxonomyData?.fields.forEach((field, index)=> {
            let name =  field.name || field.alias;
            let option = field.field_type === 1 ? field.fielditems.map(val=>val.code) : [];
            let newColumn  = {
                headerName: name?.toUpperCase(),
                field: name,
                width: 175,
                wrapText: true,
                parentField: "Parameter",
                autoHeight: true,
                resizable: true,
                flex: null,
                filter: "agTextColumnFilter",
                floatingFilter: true,
                suppressHeaderMenuButton: true,
                editable: (params) => {
                    const title = params?.data?.title;
                    return title ? !['Publisher', 'Channel'].includes(title) : true;
                },        
                
                hide: true,
                cellEditor: field.field_type === 1 ? "agRichSelectCellEditor" : "agTextCellEditor" ,
                cellEditorParams : (params) => {
                    return {values: this.getValueFromServer(params,option,field,index)}
                } , 
            };
            
            column.children.push(newColumn);
            setparameterChildColumnNames.push(name);
        });
        columnDefs.push(column);
        this.parameterChildCoulmnName = setparameterChildColumnNames;
    }

    /**
     * set option for parameter on table
     * @param {array} option 
     * @param {object} field 
     * @returns option list as array of string
     */
    getValueFromServer = (params,option,field,index) =>{
        if(field?.cascadingParentId){
            let parentFieldId = field.cascadingParentId;
            // Handle cascading parent ID logic here
            let parentField = this.defaultTaxonomyData.fields.find(val=> val.id == parentFieldId);
            let name = parentField.name || parentField.alias;
            let parentvalue = params.data[name];
            if(parentvalue !== undefined && parentvalue != null && parentvalue != '' || parentvalue != 'NU' ){
                let selectedField = parentField.fielditems.filter(val=> val.code == parentvalue)
                let updatedDefualtTaxonomy = PlacementUtils.updateDefaultTaxonomyFields(this.defaultTaxonomyData,selectedField);    
                let childField = updatedDefualtTaxonomy.find(val=> val.id == field.id);
                childField ? option = childField.fielditems.map(val=>val.code) : option;
            }
        }
        return option
    }

    /**
     * Creates the taxonomy column in the placement table.
     * This column groups all taxonomy fields under a single "TAXONOMY" header.
     */
    createTaxonomyColumn = () => {
        let uniqueIds = [];
        let columns = [];
        let fields = [];
        // Iterate through all taxonomy data to find unique taxonomy
        for(let key in this.props.allTaxonomyData){
            if(this.props.allTaxonomyData[key] && !["campaignTaxonomyData", "defaultTaxonomyData"].includes(key) && !uniqueIds.includes(this.props.allTaxonomyData[key].id)){
                uniqueIds.push(this.props.allTaxonomyData[key].id);
                columns.push(this.props.allTaxonomyData[key]);
            }
        }
        // Flatten the columns array to get all fields
        for(let i=0;i<columns.length;i++){
            if(columns[i]?.fields){
                fields.push(...columns[i]?.fields);
            }
        }
        // Remove duplicate fields
        fields = fields.filter((obj, index, self) =>
            index === self.findIndex((field) => field.id === obj.id)
        );

        let taxonomyColumn = columnDefs.find(val=> val.headerName === 'TAXONOMY')
        if(taxonomyColumn?.headerName === 'TAXONOMY' && fields?.length === taxonomyColumn?.children?.length){
            return;
        }

        taxonomyColumn = {
            headerName: 'TAXONOMY',
            children: []
        }

        fields.forEach(field=> {
            let name =  field.name || field.alias;
            let option = field.field_type === 1 ? field.fielditems.map(val=>val.code) : [];
            let newColumn  = {
                headerName: name?.toUpperCase(),
                field: name,
                parentField: "TAXONOMY",
                width: 175,
                wrapText: true,
                autoHeight: true,
                resizable: true,
                flex: null,
                filter: "agTextColumnFilter",
                floatingFilter: true,
                suppressHeaderMenuButton: true,
                editable: (params) => {
                    // Check if the field is editable based on the taxonomy data
                    const checkField = () => {
                        for(let i=0;i<params?.data?.taxonomyFieldsData?.fields?.length;i++){
                            if(params?.data?.taxonomyFieldsData?.fields[i]?.name === name && !["TAXONOMYVALUEID"].includes(name.toUpperCase())){
                                return true;
                            }
                        }
                        return false;
                    };

                    const title = params?.data?.title;
                    return title ? !['Publisher', 'Channel'].includes(title) : checkField();
                },        
                hide: true,
                cellEditor: field.field_type === 1 ? "agRichSelectCellEditor" : "agTextCellEditor" ,
                cellEditorParams : {
                    values : option
                }    
            };
            
            taxonomyColumn.children.push(newColumn);
        });
        // Find the index of the "PLACEMENT NAME" column and insert the taxonomy column after it
        let placementNameIndex = columnDefs.findIndex(col => col.headerName === 'PLACEMENT NAME');
        if (placementNameIndex !== -1) {
            columnDefs.splice(placementNameIndex + 1, 0, taxonomyColumn);
        }
    }
    
      
    /**
     * Renders the placement taxonomy information for a row in the grid.
     * If the placement_display_name is available, it is displayed; otherwise, the placement_taxonomy is shown.
    */
    placementTaxonomyRenderer = (props) => {
        if(props?.data?.placement_display_name) {
            return props.data.placement_display_name;
        }
        return props.data.placement_taxonomy;
    };

    actionCellRenderer = (props) => {
        if (!['Channel', 'Publisher'].includes(props?.data?.title)) {
            const data = props.data;
            const unpackage = data?.package_type !== "Package" && data?.package_type === "Child" && data?.package_type !== "Standalone";
            const movePlacement = !data?.package_type.includes("Package") && data?.mediaocean_status !== 'Placement Sent';
            //const showContent = unpackage || movePlacement; // Uncomment when move placement is enabled.
            const showContent = unpackage
            
            return (
                <div className="action-buttons">
                    {this.state.archiveTab ?
                        <></> :
                        <OmniTooltipNextComponent>
                            <button slot="invoker" aria-haspopup="true" className="button is-text is-shadowless"
                                onClick={() => { this.editPlacement(props) }}>
                                <OmniIconComponent className="is-size-4 icon-in-actions" icon-id="omni:interactive:edit"></OmniIconComponent>
                            </button>
                            <div slot="content">Edit</div>
                        </OmniTooltipNextComponent>
                    }

                    {(props?.data?.data_source !== 'planit' && !this.state.archiveTab) ?
                        <>
                            <OmniTooltipNextComponent>
                                <button slot="invoker" aria-haspopup="true" className="button is-text is-shadowless" onClick={() => this.handleInlineCopy(props)}>
                                    <OmniIconComponent className="is-size-4 icon-in-actions" icon-id="omni:interactive:copy"></OmniIconComponent>
                                </button>
                                <div slot="content">Copy</div>
                            </OmniTooltipNextComponent>

                            {(!props?.data?.package_type.includes("Package") && props?.data?.mediaocean_status !== 'Placement Sent') && showContent ?
                                <DropdownMenuCellRenderer
                                    {...props}
                                    id={props?.node?.id} // or another unique key
                                    isOpen={this.state.openDropdownId === props?.node?.id}
                                    onToggle={this.toggleDropdown}
                                    confirmUnPackageMsg={this.confirmUnPackageMsg}
                                    movePlacement={this.movePlacement}
                                    showContent={showContent}
                                    unpackage={unpackage}
                                    moveable={movePlacement}
                                    allPackages={this.state.rowData}
                                    handleMovePlacement={this.handleMovePlacement}
                                ></DropdownMenuCellRenderer> : <></>}
                            
                        </>
                        :
                        <></>}

                    {this.state.archiveTab ?
                        <OmniTooltipNextComponent>
                            <button slot="invoker" aria-haspopup="true" className="button is-text is-shadowless"
                                onClick={() => { this.confirmArchiveMsg(false, props?.data, 0) }}>
                                <OmniIconComponent className="is-size-4 icon-in-actions" icon-id="omni:interactive:unarchive"></OmniIconComponent>
                            </button>
                            <div slot="content">Restore</div>
                        </OmniTooltipNextComponent> :
                        <></>

                    }
                </div>
            );
        }
        return null;
    };

    mediaOpsStatusRenderer = (props) => {
        if (!['Channel', 'Publisher'].includes(props?.data?.title)) {
            const rowData = props.data;
            const isApproved = rowData?.is_approved && !(['placement sent', 'campaign sent', 'accepted', 'rejected', 'export error', 'synced'].includes(rowData?.mediaopshub_status?.toLowerCase()));
            const mediaOceanId = rowData?.mediaocean_id;

            return (
                <div>
                    {isApproved ?
                        (<span key={JSON.stringify(props?.data?.id)} className="mediaops-status-approved">Approved</span>) :
                        (<span key={JSON.stringify(props?.data?.id)} className="mediaops-status-pending">{rowData?.mediaopshub_status}</span>)}
                    
                    {mediaOceanId && (
                        <OmniTooltipNextComponent>
                            <div 
                                slot="invoker" 
                                key={`moid-${props?.data?.id}`}
                                className="media-ocean-id-display" 
                                style={{ 
                                    marginTop: '8px',
                                    fontSize: '1em',
                                    color: '#333',
                                    fontWeight: '500',
                                    padding: '3px 6px',
                                    background: '#f2f5fa',
                                    borderRadius: '4px',
                                    display: 'inline-block',
                                    border: '1px solid #e0e0e0'
                                }}>
                                Buying System ID: {mediaOceanId}
                            </div>
                            <div slot="content">This ID matches the placement in the buying system (Prisma)</div>
                        </OmniTooltipNextComponent>
                    )}
                </div>
            );
        }
        return null;
    };

    netMediaRenderer = (props) => {
        const isLocal = this.props?.systemParametersData?.data?.isLocal;
        const currency = isLocal && this.state?.campaignObj?.local_currency_name ? this.state?.campaignObj?.local_currency_name : 'USD';
        let plannedCostNumber = isLocal ? props?.data?.planned_cost_local : props?.data?.planned_cost;
        let displayValue = PlacementDetailsTableService.getNumberWithTwoDigitsAfterDecimal(plannedCostNumber, false, currency)
        if (!['Channel', 'Publisher'].includes(props?.data?.title)) {
            return (
                <div>
                    {['Child', 'DirectCreative', 'DirectLine'].includes(props?.data?.package_type) ?
                        (<></>) :
                        (<span>{displayValue}</span>)}
                </div>
            )
        } else {
            return (<div>
                <span>{displayValue}</span>
            </div>)
        }
    }

    rateRenderer = (props) => {
        const isLocal = this.props?.systemParametersData?.data?.isLocal;
        const currency = isLocal && this.state?.campaignObj?.local_currency_name ? this.state?.campaignObj?.local_currency_name : 'USD';
        if (!['Channel', 'Publisher'].includes(props?.data?.title)) {
            const displayValue = PlacementDetailsTableService.getRate(props.data, isLocal, currency);
            return (
                <div>
                    {!displayValue ?
                        (<></>) :
                        (<span>{displayValue}</span>)}
                </div>
            )
        }
    }

    unitRenderer = (props) => {
        const displayValue = PlacementDetailsTableService.getUnits(props.data);
        return (<span>{displayValue}</span>)
    }

    adServingFeeRenderer = (props) => {
        const value = props?.data;
        if (['Channel'].includes(value?.title)) {
            return this.channelSiteMap[value?.placement_taxonomy] ? this.channelSiteMap[value?.placement_taxonomy]?.ad_server_fee.toLocaleString('en-US', { style: "currency", currency: "USD" }) : ''
        } else if (['Publisher'].includes(value?.title)) {
            return this.channelSiteMap[value?.channel] ? (this.channelSiteMap[value?.channel][value?.placement_taxonomy] ?
                this.channelSiteMap[value?.channel][value?.placement_taxonomy]?.ad_server_fee.toLocaleString('en-US', { style: "currency", currency: "USD" }) : '') : '';
        } else {
            if (value?.ad_server_fee || value?.ad_server_fee === 0) {
                return PlacementDetailsTableService.getAdServerFee(value);
            } else {
                return '';
            }
        }
    }

    adVerificationFeeRenderer = (props) => {
        const value = props?.data;
        if (['Channel'].includes(value?.title)) {
            return this.channelSiteMap[value?.placement_taxonomy] ? this.channelSiteMap[value?.placement_taxonomy]?.ad_verification_fee.toLocaleString('en-US', { style: "currency", currency: "USD" }) : ''
        } else if (['Publisher'].includes(value?.title)) {
            return this.channelSiteMap[value?.channel] ? (this.channelSiteMap[value?.channel][value?.placement_taxonomy] ?
                this.channelSiteMap[value?.channel][value?.placement_taxonomy]?.ad_verification_fee.toLocaleString('en-US', { style: "currency", currency: "USD" }) : '') : '';
        } else {
            if (value?.ad_verification_fee || value?.ad_verification_fee === 0) {
                return PlacementDetailsTableService.getAdVerificationFee(value);
            } else {
                return '';
            }
        }
    }

    totalMediaRenderer = (props) => {
        const value = props?.data;
        const isLocal = this.props.systemParametersData.data.isLocal;
        const currency = isLocal && this.state.campaignObj.local_currency_name ? this.state.campaignObj.local_currency_name : 'USD';

        let plannedCost = null;
        let adServingBudget = null;
        let adVerifiactionBudget = null;
        if (isLocal && currency) {
            plannedCost = value?.planned_cost_local || 0;
        } else {
            plannedCost = value?.planned_cost || 0;
        }

        if (value.ad_server_fee) {
            adServingBudget = value.ad_server_fee;
        } else if (value.ad_server_fee === 0) {
            adServingBudget = 0;
        }

        if (value.ad_verification_fee) {
            adVerifiactionBudget = value.ad_verification_fee;
        } else if (value.ad_verification_fee === 0) {
            adVerifiactionBudget = 0;
        }

        if (plannedCost === null && adServingBudget === null && adVerifiactionBudget === null) {
            return '';
        } else {
            const totalValue = plannedCost + adServingBudget + adVerifiactionBudget;
            return totalValue.toLocaleString('en-US', { style: 'currency', currency: currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
    }

    /**
     * Renders the planning system amount based on the item's title (Channel or Publisher),
     * whether the system is local, and the currency.
     *
     * @param {object} props - The component's props.
     * @param {object} props.data - The data object containing item details.
     * @param {string} props.data.title - The title of the item (e.g., 'Channel', 'Publisher').
     * @param {number} props.data.channel_total_budget_in_pmds_local - The channel total budget in local currency.
     * @param {number} props.data.channel_total_budget_in_pmds - The channel total budget in USD.
     * @param {number} props.data.publisher_budget_in_pmds_local - The publisher budget in local currency.
     * @param {number} props.data.publisher_budget_in_pmds - The publisher budget in USD.
     * @returns {string} The formatted currency string or an empty string if the item is not a Channel or Publisher.
     */
    planningSysytemAmountRenderer = (props) => {
        const item = props?.data;
        const isLocal = this.props.systemParametersData.data.isLocal;
        const currency = isLocal && this.state.campaignObj.local_currency_name ? this.state.campaignObj.local_currency_name : 'USD';

        if (item?.title === 'Channel') {
            if (isLocal && currency) {
                return item?.channel_total_budget_in_pmds_local?.toLocaleString('en-US', { style: 'currency', currency: currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })
            } else {
                return item?.channel_total_budget_in_pmds?.toLocaleString('en-US', { style: 'currency', currency: currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })
            }
        } else if (item?.title === 'Publisher') {
            if (isLocal && currency) {
                return item?.publisher_budget_in_pmds_local?.toLocaleString('en-US', { style: 'currency', currency: currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })
            } else {
                return item?.publisher_budget_in_pmds?.toLocaleString('en-US', { style: 'currency', currency: currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })
            }
        } else {
            return '';
        }
    }

    placementTypeRenderer = (props) => {
        return PlacementUtils.getPlacementTypeDisplayValue(props.data);
    }

    //Un Package Alert Message
    confirmUnPackageMsg = (event) => {
        const data = this.gridRef?.current?.api?.getRowNode(event)?.data;
        om2confirm("This action will unpackage the selected placements. Are you sure you want to do this?", {
            callback: () => this.unPackage(data),
            type: "warning",
            title: "Unpackage Placements"
        });
    }
    /**
     * The function unPackage moves a placement package and updates the state accordingly.
     * @param placement - an object representing a placement
     * @param prefix - The prefix parameter is not used in the given code snippet. It is not relevant
     * to the unPackage function.
     * @param tempIndex - tempIndex is a variable that is not used in the given code snippet. It is not
     * passed as a parameter to the unPackage function and is not referenced anywhere within the
     * function.
     */
    unPackage(placement){
        this.props.setSpinnerCount(1);
        const payload = {
            "clientId": Utils.getRouteParam("clientid"),
            "parentId": '',
            "packageType": "Standalone",
            "updated_by": this.props && this.props.omniProfile && this.props.omniProfile['email']
        }
        
        let campaignId = encodeURIComponent(this.state.campaignObj.id);
        MediaOpsService.movePlacement(campaignId, placement.id, payload, this.props?.versionOneApi).then(result => {
            AlertMessagesService.showSuccessToastMessage('You have successfully unpacked a placement package.');
            this.loadPlacements();
        }, (error) => {
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.unPackage(placement), title: 'Unpackage API cannot be loaded', bodyMsgArr: []}]);
            this.props.setSpinnerCount(0);
        });
    }

    // on page load checks for the campaign obj and if it is not available 
    // an api apicall is made with the campaign id from query param from the route
    pageInit = () => {
        // resetting channel 
        this.campaignChannelDropdown = [];
        this.channelDropdownList = [{ title: "Create", submenu: [] }];
        if (this.props?.state?.campaignObj?.id) {
            let campaignObj = this.props.state.campaignObj;
            this.setChannelForMenu(campaignObj);
        } else {
            setTimeout(() => {
                let campaignId = encodeURIComponent(this.props?.params?.campaignId);
                MediaOpsService.getCampaignDataById(campaignId, Utils.getRouteParam("clientid"), this.props?.versionOneApi)
                    .then((campaignData) => {
                        if(!campaignData || !campaignData.length){
                            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Refresh', retryBtnEvent: () => this.pageInit(), title: 'Campaign details not found', bodyMsgArr: [] }]);
                            this.setState({ loading: false });
                            return;
                        }
                        // if campaign has parent id then fetch parent campaign details
                        if (campaignData[0]?.parent_id) {
                            this.getParentCampaign(campaignData);
                        } else {
                            this.updateChannelForMenu(campaignData);
                        }
                    })
                    .catch((error) => {
                        const errorMessage = error?.response?.data?.message || error?.response?.data?.errorMessage || error?.message || '';
                        AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Refresh', retryBtnEvent: () => this.pageInit(), title: 'Unable to load the campaign details API', bodyMsgArr: [errorMessage] }]);
                    });
            }, 100);
        }
        //set import placement button 
        this.addFileUploadEventHandler();
    };

    /**
     * Fetches and processes the parent campaign data for the given campaign.
     *
     * - Retrieves the parent campaign using its ID from the provided campaign data.
     * - Updates the campaign data with the parent's local currency name (defaulting to 'USD' if not present).
     * - Sets the campaign's budget and channels from its media attributes.
     * - Initializes the supplier field taxonomy based on campaign partners.
     * - Calls `setChannelForMenu` to update the menu based on the campaign object.
     * - Handles errors by displaying an alert with a retry option.
     *
     * @param {Array<Object>} campaignData - The campaign data array, where the first element should contain the `parent_id` and media attributes.
     */
    getParentCampaign = (campaignData) => {
        const parentCampaignId = encodeURIComponent(campaignData[0].parent_id);
        MediaOpsService.getCampaignDataById(parentCampaignId, Utils.getRouteParam("clientid"), this.props?.versionOneApi)
            .then((parentCampaignData) => {
                campaignData[0]['local_currency_name'] = parentCampaignData[0]['local_currency_name'] || 'USD';
                this.updateChannelForMenu(campaignData);
            })
            .catch((error) => {
                const errorMessage = error?.response?.data?.message || error?.response?.data?.errorMessage || error?.message || '';
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Refresh', retryBtnEvent: () => this.getParentCampaign(campaignData), title: 'Unable to load the parent campaign details API', bodyMsgArr: [errorMessage] }]);
            });
    }

    /**
     * Updates the channel and supplier field taxonomy for the menu based on the provided campaign data.
     *
     * - Sets the campaign budget and channels from the media attributes.
     * - Constructs the supplier field taxonomy object using partners from the campaign data.
     * - Calls `setChannelForMenu` with the updated campaign object.
     *
     * @param {Array<Object>} campaignData - An array containing campaign data objects. The first object is used to update fields.
     */
    updateChannelForMenu = (campaignData) => {
        campaignData[0]['budget'] = campaignData[0]['media_attributes']['media_budget']['campaign_budget'] || '';
        campaignData[0]['channels'] = campaignData[0]['media_attributes']['channels'] || '';
        let campaignObj = campaignData[0];
        this.supplierFieldTaxonomy = {
            field_type: 1,
            name: 'Supplier',
            alias: 'Supplier',
            delimiter_value: "_",
            hidden: false,
            isExplicitCascading: true,
            fielditems: (campaignObj?.partners || []).map(item => ({ name: item, code: item }))
        };
        this.setChannelForMenu(campaignObj)
    }

    /**
     * set channel in Menu for create placement/package
     * @param {object} campaignObj 
     */
    setChannelForMenu = (campaignObj) => {
        let channelList = ["Display", "Video", "Audio", "Paid Social", "Paid Search", "Fee type", "Print", "OOH", "Cinema", "Other", "Unallocated", "National TV", "Network Radio", "Spot TV", "Spot Radio"];
        campaignObj?.channels?.length && campaignObj?.channels?.forEach((ele, index) => {
            //we are ignoring other tech from publisher
            let publishers = ele?.publishers?.filter(val => val.publisher_type !== "Other Fees");
            ele.publishers = publishers;
            this.setChannelDropdown(ele, channelList);
            if (index === campaignObj.channels.length - 1) {
                this.setCreateDropdown(channelList);
            }
        });
        
        // Only set campaign name and navigated campaign obj if they exist
        if (this.props.setCampaignName && this.props?.state?.campaignObj?.campaign_name) {
            this.props.setCampaignName(Utils.formatCampaignTitle(this.props.state.campaignObj));
        }
        
        if (this.props.setNavigatedCampaignObj && this.props?.state?.campaignObj) {
            this.props.setNavigatedCampaignObj(this.props.state.campaignObj);
        }

        this.setState({ campaignObj: campaignObj }, () => {
            this.loadVendorMapping(campaignObj);
            this.props.setCampaignName(Utils.formatCampaignTitle(campaignObj));
            this.props.navigate(this.props.location.pathname, {
                replace: true,
                state: { ...this.props.location.state, campaignObj },
            });
        });
    }

    /**
     * This function is used to filter the channel dropdown data based on the selected channel
     * this will store the selected campaign channel in the 'campaignChannelDropdown' array and remove it from the channel list
     * @param {object} channel - selected channel object
     * @param {array} channelList - list of all channels
    */
    setChannelDropdown = (channel, channelList) => {
        for (let i = 0; i < channelList.length; i++) {
            if (channelList[i] === channel?.channel_name?.trim()) {
                this.campaignChannelDropdown.push(channelList[i]);
                channelList.splice(i, 1);
                break;
            }
        }
    }

    /**
     * This function is used to set the channel dropdown list
     * First it will iterate over campaignChannelDropdown list and then the remaining channels
     * @param {array} channelList - list of all channels
    */
    setCreateDropdown = (channelList) => {
        for (let i = 0; i < this.campaignChannelDropdown.length; i++) {
            PlacementUtils.setMenuTypes(this.campaignChannelDropdown[i], this.channelDropdownList, this.allTaxonomyData);
        }
        if (this.campaignChannelDropdown.length) {
            this.channelDropdownList[0].submenu[this.channelDropdownList[0].submenu.length - 1].separator = true;
        }
        for (let i = 0; i < channelList.length; i++) {
            PlacementUtils.setMenuTypes(channelList[i], this.channelDropdownList, this.allTaxonomyData);
        }
        this.setDisabledMenuItems(this.channelDropdownList, null);
        this.setTaxonomyAndChannel(this.channelDropdownList);
    }

    /**
     * This function is used to set the disabled menu items based on the taxonomy data
     * @param {array} createMenuList - list of all the create menu items
     * @param {object} enableList - object containing the enable/disable status for each of the menu items
    */
    setDisabledMenuItems = (createMenuList, enableList) => {
        for (let i = 0; i < createMenuList.length; i++) {
            if (createMenuList[i].submenu?.length) {
                this.setDisabledMenuItems(createMenuList[i].submenu, enableList ? enableList : null);
            } else {
                createMenuList[i].disabled = !this.allTaxonomyData[createMenuList[i].taxonomyData];
                this.disableDirectPlacementMeanu(createMenuList, enableList, i);
                this.disableProgramaticPlacementMeanu(createMenuList, enableList, i)
            }
        }
    }

    disableDirectPlacementMeanu = (createMenuList, enableList, i) => {
        if (createMenuList[i].type.includes("Direct") && ["Line Item", "Creative Placement"].includes(createMenuList[i].title)) {
            switch (createMenuList[i].title) {
                case "Line Item":
                    if (!createMenuList[i].disabled) {
                        createMenuList[i].disabled = enableList ? !enableList.enableDirectLine : true;
                    }
                    break;
                case "Creative Placement":
                    if (!createMenuList[i].disabled) {
                        createMenuList[i].disabled = enableList ? !enableList.enableDirectCreative : true;
                    }
                    break;
                default:
                    break;
            }
        }
    }

    disableProgramaticPlacementMeanu = (createMenuList, enableList, i) => {
        if (createMenuList[i].type.includes("Programmatic") && createMenuList[i].title !== 'Package') {
            switch (createMenuList[i].title) {
                case "Placement":
                    if (!createMenuList[i].disabled) {
                        createMenuList[i].disabled = enableList ? !enableList.enableProgrammaticPlacement : true;
                    }
                    break;
                case "Line Item":
                    if (!createMenuList[i].disabled) {
                        createMenuList[i].disabled = enableList ? !enableList.enableProgrammaticLine : true;
                    }
                    break;
                case "Creative Placement":
                    if (!createMenuList[i].disabled) {
                        createMenuList[i].disabled = enableList ? !enableList.enableProgrammaticCreative : true;
                    }
                default:
                    break;
            }
        }
    }

    /**
     * This function is used to set the taxonomy map and the channel for each of the create menu items
     * This is a recursive function which will set the taxonomy data for each of the submenu items and store it in the 'placementTaxonomyMap'
     * @param {array} createMenuList - list of all the create menu items
     * @param {string} channel - channel name
    */
    setTaxonomyAndChannel = (createMenuList, channel = null) => {
        for (let i = 0; i < createMenuList.length; i++) {
            if (createMenuList[i].submenu?.length) {
                this.setTaxonomyAndChannel(createMenuList[i].submenu, createMenuList[i].channel ? createMenuList[i].channel : channel);
            } else {
                if (!this.placementTaxonomyMap[createMenuList[i].title]) {
                    this.placementTaxonomyMap[createMenuList[i].title] = {
                        [createMenuList[i].type]: {}
                    };
                } else if (!this.placementTaxonomyMap[createMenuList[i].title][createMenuList[i].type]) {
                    this.placementTaxonomyMap[createMenuList[i].title][createMenuList[i].type] = {};
                }
                createMenuList[i].channel = channel ? channel : createMenuList[i].channel;
                this.placementTaxonomyMap[createMenuList[i].title][createMenuList[i].type] = createMenuList[i].taxonomyData ? this.allTaxonomyData[createMenuList[i].taxonomyData] : {};
            }
        }
    }

    resetStateAndSendAlerts = (respMsgArr) => {
        AlertMessagesService.showMultipleAlerts(respMsgArr);
        this.gridRef.current.api.deselectAll('all');
        this.loadPlacements();
    }

    resetSelctedItems = () => {
        // this.setState({ selectedItems: [], spinnerCount: 0 });
    }

    getSelectedRows = () => {
        const selectedNodes = new Set(); // Use a Set to avoid duplicates
        this.gridRef.current.api.forEachNode((node) => {
            if (node.isSelected()) {
                this.addNodeAndAncestors(node, selectedNodes);
            }
        });
        const selectedArray = Array.from(selectedNodes);
        return selectedArray;
    };

    addNodeAndAncestors = (node, selectedNodes) => {
        while (node) {
            if (node.data && !['Channel', 'Publisher'].includes(node?.data?.title)) {
                // Check if all children are selected
                const allChildrenSelected = node.childrenAfterGroup?.every(child => child.isSelected());
                if (!allChildrenSelected) {
                    break; // Stop adding ancestors if not all children are selected
                }
                selectedNodes.add(node.data);
            }
            node = node.parent; // Move to the parent node, continue until no parent exists
        }
    };

    getSelectedRowsWithFewChildrenSelected = () => {
        const selectedNodes = new Set(); // Use a Set to avoid duplicates
        this.gridRef.current.api.forEachNode((node) => {
            if (node.isSelected()) {
                this.addNodeAndAncestorsFewChildrenSelected(node, selectedNodes);
            }
        });
        const selectedArray = Array.from(selectedNodes);
        return selectedArray;
    }

    addNodeAndAncestorsFewChildrenSelected = (node, selectedNodes) => {
        while (node) {
            if (node.data && !['Channel', 'Publisher'].includes(node?.data?.title)) {
                selectedNodes.add(node.data);
            }
            node = node.parent; // Move to the parent node, continue until no parent exists
        }
    };

    updateAllTaxonomyData = () => {
        const defaultTaxonomyFIeldArray = [];
        this.props.allTaxonomyData?.defaultTaxonomyData.fields?.map((item) => { // Adding Supplier field to the default taxonomy fields
            if (this.props?.state?.campaignObj?.partners?.length && item.name === 'Supplier') {
                defaultTaxonomyFIeldArray.push(this.supplierFieldTaxonomy);
            } else if (!this.props.allTaxonomyData?.defaultTaxonomyData.fields?.some(field => field.name === 'Supplier') && item.name === 'Supplier Code') {
                defaultTaxonomyFIeldArray.push(this.supplierFieldTaxonomy);
                defaultTaxonomyFIeldArray.push(item);
            } else {
                defaultTaxonomyFIeldArray.push(item);
            }
        })
        this.allTaxonomyData = { ...this.props.allTaxonomyData, defaultTaxonomyData: { ...this.props.allTaxonomyData?.defaultTaxonomyData, fields: defaultTaxonomyFIeldArray } };
        this.createTaxonomyStrings();
    }

    createTaxonomyStrings = () => {
        let taxonomyStringObj = {
            defaultplacement: "defaultTaxonomyData",
            directpackage: "directPackageTaxonomyData",
            placement: "placementTaxonomyData",
            directcreative: "directCreativeTaxonomyData",
            programmaticpackage: "programmaticPackageTaxonomyData",
            programmaticplacement: "programmaticPlacementTaxonomyData",
            dsppackage: "dspPackageTaxonomyData",
            creativepackage: "creativePackageTaxonomyData", // programmatic creative
            socialdirectpackage: "socialDirectPackageTaxonomyData",
            socialdirectplacement: "socialDirectPlacementTaxonomyData",
            socialdirectcreative: "socialDirectCreativeTaxonomyData",
            socialprogrammaticpackage: "socialProgrammaticPackageTaxonomyData",
            socialprogrammaticplacement: "socialProgrammaticPlacementTaxonomyData",
            socialprogrammaticline: "socialProgrammaticLineItemTaxonomyData",
            socialprogrammaticcreative: "socialProgrammaticCreativeTaxonomyData",
            search: "searchPackageTaxonomyData",
            feeorder: "feeOrderTaxonomyData",
            offlinepackage: "offlinePackageData",
            offlineestimatename: "offlineEstimateNameData",
            doohpackage: "oohPackageData",
            doohplacement: "oohEstimateNameData",
            networkttvpackage: "networkTvPackageData",
            networktvestimatename: "networkTvEstimateNameData"
        }

        for (const key in taxonomyStringObj) {
            if (Object.hasOwnProperty.call(taxonomyStringObj, key)) {
                const element = taxonomyStringObj[key];
                this.taxonomyStrings[key] = PlacementDetailsTableService.createTemplateString(this.allTaxonomyData[element]?.fields);
            }
        }
    }

    //saving the restoring the checked rows after updating the data inside the table

    saveSelectedRows = () => {
        const selectedNodes = this.gridRef?.current?.api?.getSelectedNodes();
        const selectedIds = selectedNodes?.map(node => node?.data?.id);
        this.setState({ selectedIds });
    };

    restoreSelectedRows = () => {
        const { selectedIds } = this.state;
        if (!selectedIds || selectedIds.length === 0) return;

        this.gridRef?.current?.api?.forEachNode((node) => {
            if (selectedIds?.includes(node.data.id)) {
                node.setSelected(true);
            }
        });
    };

    /**
     * Opens the Edit Template modal with the provided placement data
     * @param {Object} placementData - The placement data to edit the template for
     */
    openEditTemplateModal = () => {
        this.setState({
            editTemplateModalOpen: true
        });
    };

    /**
     * Closes the Edit Template modal and resets the template data
     */
    closeEditTemplateModal = () => {
        this.setState({
            editTemplateModalOpen: false
        });
    };

    /**
     * If it's child campaign setting params for PMDS
     * @returns params inlcuding PMDS true
     */
    setFilterForChildCampaign = () => {
        // The below code is defining a variable called `selectedCampaign` and setting it to the value of the`campaignObj` property of the component's state.
        const selectedCampaign = this.state?.campaignObj;
        let filterObj = {};
        if (selectedCampaign?.parent_id) {
            let parentCampaignId = encodeURIComponent(selectedCampaign.parent_id);
            let channels = selectedCampaign?.channels?.map(val=>val.channel_name);
            let channelSuppliers = '';
            selectedCampaign?.channels.forEach((channel,i)=>{
                let allpublisher = channel.publishers.map(val=>val.publisher_name);
                let colon = channels.length-1 ===i ? '':";"
                channelSuppliers = channelSuppliers + channel.channel_name+":"+allpublisher+ colon

            });

            filterObj = {
                parent_amd_campaign_id: parentCampaignId,
                firlterParams:{campaign_channels: channels.join(','),channel_suppliers:channelSuppliers,campaign_id: selectedCampaign?.synthetic_id},
            }
            // Adding a filter parameter to the filter object based on the data source and PMDS ingestion status of the selected campaign
            if(selectedCampaign.data_source === 'MediaOps' && selectedCampaign.is_pmds_ingestion){
                filterObj.firlterParams.is_pmds_ingestion = true;
            } else if(selectedCampaign.data_source === 'MediaOps' && !selectedCampaign.is_pmds_ingestion) {
                filterObj.firlterParams.is_pmds_ingestion = false;
            }
        }

        filterObj.page_no = 1;
        filterObj.page_size = 2500;
        filterObj["pmds-version"] = "v3";
        return filterObj;
    }
    // loading the placements and vendormapping data
    loadPlacements = (firstLoad = false, refreshCampaign = false) => {
        let placementData = this.props?.placementsData;
        let amdCampaignId  = placementData?.length ? placementData[0]?.amd_campaignid : '';
        if(placementData && placementData.length && firstLoad && amdCampaignId && amdCampaignId === this.state?.campaignObj?.id){
            this.setState({ loading: false });
            this.setPlacementData(placementData);
            this.callPlacementAPI(firstLoad, refreshCampaign);
        }else{
            this.props.setSpinnerCount(1);
            this.callPlacementAPI(firstLoad, refreshCampaign);
        }
    }

    /**
     * Calling placement API to get the placement data
     * @param {Boolean} firstLoad
     * @param {Boolean} refreshCampaign - when true, refresh the campaign data after the placement data is loaded
     */
    callPlacementAPI = (firstLoad, refreshCampaign = false) => {
        this.saveSelectedRows();
        let filterObj = this.setFilterForChildCampaign();
        const campaignId = this.state?.campaignObj?.id ? this.state?.campaignObj?.id : this.props?.params?.campaignId;
        this.getPlacement(firstLoad,campaignId,filterObj,[],null,refreshCampaign);
    }
    /**
     * calling placement get api as pagination if all data didn't came in one call
     * @param {boolean} firstLoad
     * @param {number} campaignId
     * @param {object} filterObj
     * @param {boolean} refreshCampaign - when true (placement create/update), call the campaign get api after the placement get api completes
     */
    getPlacement = (firstLoad,campaignId,filterObj,totalPlacementData = [], hasNext = null, refreshCampaign = false) => {
        // Always call the raw paginated API from the Placements AG Grid table.
        // Table-triggered network calls must not use the cached aggregated wrapper.
        const call = firstLoad
            ? PlacementDetailsTableService.getPlacementsWithDeliveredCached(campaignId, { is_delete: this.state.archiveTab, campaign_id: this.state?.campaignObj?.synthetic_id, ...filterObj }, this.props.versionOneApi)
            : PlacementDetailsTableService.getPlacementsWithDelivered(campaignId, { is_delete: this.state.archiveTab, campaign_id: this.state?.campaignObj?.synthetic_id, ...filterObj }, this.props.versionOneApi, hasNext);

        call.then((response) => {
            totalPlacementData.push(...response?.data?.Items);
            let nextCursor = response?.data?.paging?.next;
            if(!nextCursor){
                this.setPlacementData(totalPlacementData, firstLoad);
                if(refreshCampaign){
                    // refresh campaign level data in the placement table after placement create/update
                    this.loadCampaignObj();
                }
            } else {
                this.getPlacement(firstLoad,campaignId,filterObj,totalPlacementData,nextCursor,refreshCampaign);
            }
        }, (error) => {
            const errorMessage = error?.response?.data?.error?.message;
            AlertMessagesService.showMultipleAlerts([{
                type: 'danger',
                retryBtnText: 'Retry',
                retryBtnEvent: () => this.loadPlacements(firstLoad, refreshCampaign),
                title: 'Placement API cannot be loaded',
                bodyMsgArr: errorMessage ? [html`<b>Error description</b>:<br/>${errorMessage}`] : [],
            }]);
            this.props.setSpinnerCount(0);
        }).finally(() => {
            if(firstLoad) {
                this.clearPlacementCacheBackground();
                //this function will call on placement page load
                this.getAdServeingAndAdVerifcationRateAndFee();
            }
        })
    }

    
    /**
     * Compare new placements list with cached (props) placements to detect changes.
     * This function performs a shallow comparison of two placement lists by comparing
     * their lengths and then comparing placement IDs at each index. It returns true
     * when a difference is detected and false when both lists appear identical.
     * @param {Array<Object>} newPlacementsList - Latest placements returned from the API
     * @returns {boolean} isDataChanged - true when lists differ, false when they appear the same
     */
    comparePlacementsWithCashedData = (newPlacementsList) => {
        let isDataChanged = false;
        let oldPlacementData = this.props?.placementsData || [];

        if (oldPlacementData.length === newPlacementsList?.length) {
            for (let i = 0; i < oldPlacementData.length; i++) {
                if (oldPlacementData[i].id !== newPlacementsList[i].id) {
                    isDataChanged = true;
                    break;
                }
            }
        }else{
            isDataChanged = true;
        }
        return isDataChanged;
    }

    /**
     * Set placement data for table after all Placement API call.
     * @param {Array} placementData - The placement data returned from the API
     * @param {boolean} firstLoad - Indicates if this is the first load of placements
     */
    setPlacementData = (placementData, firstLoad) => {
        let placementsFromApi = [];
        let placementsWithNestedStructureWithOutEmptyChannels = [];
        let emptyChannelsAndPublishers = PlacementDetailsTableService.createEmptyChannelsData(this.state.campaignObj);
        let placementsWithNestedStructureWithEmptyChannels = []

        if (placementData?.length) {
            placementsFromApi = placementData;
            placementsFromApi.forEach((item, itemIndex) => {
                // format date with and without timestamp
                if(typeof item['flight_start'] === 'string'){
                    item['flight_start'] = item['flight_start'] ? PlacementUtils.parseDate(item['flight_start']) : '';
                }
                if(typeof item['flight_end'] === 'string'){
                    item['flight_end'] = item['flight_end'] ? PlacementUtils.parseDate(item['flight_end']) : '';
                }
                if (item?.placement_type?.toLowerCase() === 'social') {
                    item['placement_type'] = 'Paid Social';
                } else if (item?.placement_type?.toLowerCase() === 'search') {
                    item['placement_type'] = 'Paid Search';
                }

                // find the editable data of all taxonomy fields and assign to item
                let taxonomySchemaArray = PlacementUtils.getDialogTaxonomyData(this.state, PlacementUtils.mapPackageType(item.package_type, item.placement_type, item.parent_type), this.placementTaxonomyMap, this.allTaxonomyData);
                let { editFormData } = PlacementDetailsTableService.getFormEditData(taxonomySchemaArray?.fields, item);
                Object.assign(item, editFormData);
                item.taxonomyFieldsData = taxonomySchemaArray;
                let parameterData = {};
                if(item["parameter_code"]){
                    parameterData = PlacementDetailsTableService.splitPlacementStringIntoSchemaObject(item["parameter_code"], item["parameter_code_taxonomy_schema"], this.allTaxonomyData.defaultTaxonomyData?.fields);
                }else{
                    parameterData = PlacementDetailsTableService.getDefaultValuesForParameterCode( this.allTaxonomyData.defaultTaxonomyData?.fields);
                }
                item.parameterCodeSplitValues = []
                Object.keys(parameterData).forEach(val=>{
                    if(!parameterData[val]){
                        delete parameterData[val];
                    }else{
                        item[val] = parameterData[val];
                        item.parameterCodeSplitValues.push({name: val, value: parameterData[val],});
                    }
                });
                
                if (item?.planned_mapped_channel?.toLowerCase() === 'social') {
                    item['planned_mapped_channel'] = 'Paid Social';
                } else if (item?.planned_mapped_channel?.toLowerCase() === 'search') {
                    item['planned_mapped_channel'] = 'Paid Search';
                }

                // to highlight the newly created/updated rows
                const loginTime = new Date(new Date(CookieService.getCookie("LoginTime")).toUTCString());
                const createTime = new Date(item?.created_at + 'Z');
                const upateTime = new Date(item?.updated_at + 'Z');
                if ((loginTime < createTime && item?.created_by === this.props.omniProfile?.email) || (loginTime < upateTime && item?.updated_by === this.props.omniProfile?.email)) {
                    item['highlight'] = true;
                }
                if (itemIndex + 1 === placementsFromApi.length) {
                    this.channelSiteMap = PlacementUtils.calculateTotalCostAndUnits(placementsFromApi, this.vendorMappingMap, this.props?.state?.campaignObj);
                    placementsWithNestedStructureWithOutEmptyChannels = PlacementUtils.CreateNestedData(placementsFromApi, this.vendorMappingMap, this.props?.systemParametersData?.data?.isLocal, this.channelSiteMap, this.props?.state?.campaignObj);
                    placementsWithNestedStructureWithEmptyChannels = PlacementUtils.mergeChannelsAndPublishers(placementsWithNestedStructureWithOutEmptyChannels, emptyChannelsAndPublishers);
                }
            })
        }
        else {
            placementsWithNestedStructureWithEmptyChannels = emptyChannelsAndPublishers;
        }

        placementsWithNestedStructureWithEmptyChannels = this.updatePlanitValues(placementsWithNestedStructureWithEmptyChannels);
        this.setState({
            placementsFromApi: placementsFromApi,
            placementsWithNestedStructure: placementsWithNestedStructureWithEmptyChannels,
            selectedItems: [],
        }, () => {
            this.setState({ loading: false });
            setTimeout(async () => {
                // Check for supplier in URL, if present, apply filter with supplier name in placement_taxonomy field. 
                const { supplierName } = this.props?.params || {};
                const isPlacementFromGlobalSearch = this.props?.state?.navigateFromGlobalSearch && this.props?.state?.placement;
                if ((supplierName || isPlacementFromGlobalSearch) && firstLoad) {
                    await this.gridRef?.current?.api?.setColumnFilterModel('placement_taxonomy', {
                        filterType: 'text',
                        type: 'contains',
                        filter: supplierName || this.props?.state?.placement.toString(),
                    });
                    this.gridRef?.current?.api?.onFilterChanged();
                }
                this.gridRef?.current?.api?.closeToolPanel();
                this.gridRef?.current?.api?.refreshCells({ force: true, });
                this.restoreSelectedRows();
                this.props.setSpinnerCount(0);
            }, 100);
        });
    }
    
    /**
     * Lazy loading with async computation for smart suggestions.
     * Only computes when dialog opens for the first time, then caches result.
     * @param {string} placementType - The placement type (e.g., 'Direct', 'Programmatic')
     * @param {function} callback - Optional callback to notify when computation completes
     * @returns {object|null|undefined} - Suggestions data if ready, null if no data found, undefined if loading
     */
    computeSmartSuggestionsLazy = (placementType, taxonomyData, callback = null) => {
        const cacheEntry = this.smartSuggestionsCache[placementType];
        
        // Already computed - return immediately (could be data object or null if no suggestions)
        if (cacheEntry?.status === 'ready') {
            return cacheEntry.data;
        }
        
        // Currently computing - return undefined to show loading state
        if (cacheEntry?.status === 'loading') {
            return undefined;
        }
        
        // First time - start async computation
        const personEmail = this.props.omniProfile?.email;
        
        if (!personEmail || this.state.placementsFromApi.length === 0) {
            return null;
        }
        
        // Mark as loading
        this.smartSuggestionsCache[placementType] = { status: 'loading', data: null };
        
        // Async computation (non-blocking)
        setTimeout(() => {
            try {
                const taxonomyFields = taxonomyData?.fields || [];
                const placementToBeAnalyzed = this.state.placementsFromApi.filter(placement => placement.data_source !== 'planit');
                
                const suggestions = PlacementUsageAnalytics.analyzePlacementsAndGetSuggestions(
                    placementToBeAnalyzed,
                    personEmail,
                    placementType,
                    taxonomyFields
                );
                
                // Cache the result
                this.smartSuggestionsCache[placementType] = {
                    status: 'ready',
                    data: suggestions
                };
                
                // Notify callback if provided
                if (callback) {
                    callback(suggestions);
                }
            } catch (error) {
                console.error('Error computing smart suggestions:', error);
                this.smartSuggestionsCache[placementType] = {
                    status: 'ready',
                    data: null
                };
            }
        }, 0);
        
        return undefined; // Return undefined to indicate loading state
    }
    /**
     * Updates the placements array with channel and publisher budget values from the campaign object.
     *
     * This method iterates through each placement in the provided `placements` array and attempts to merge
     * budget information from the `channels` array within the component's state. It matches channels
     * based on `channel_name` and placement taxonomy, and publishers based on `publisher_name` and
     * nested placement taxonomy. If a match is found, the corresponding budget values are added to the
     * placement and nested placement objects.
     *
     * @param {Array<Object>} placements - An array of placement objects to be updated. Each object should
     * have a `placement_taxonomy` property for channel matching and a `nestedData` array for publisher matching.
     * @returns {Array<Object>} A new array with updated placement objects containing budget information, or
     * the original placements if no updates are necessary.
     */
    updatePlanitValues = (placements) => {
        const channels = this.state.campaignObj?.media_attributes?.channels;
        const output = placements.map(placement => {
            const allMatchingChannels = channels.filter(c => c.channel_name === placement.placement_taxonomy);
            let channelTotalBudgetInPmds = allMatchingChannels.length > 0 ? allMatchingChannels.reduce((acc, curr) => acc + (curr.channel_total_budget_in_pmds || 0), 0) : 0;
            let channelTotalBudgetInPmdsLocal = allMatchingChannels.length > 0 ? allMatchingChannels.reduce((acc, curr) => acc + (curr.channel_total_budget_in_pmds_local || 0), 0) : 0;
            const mergedPlacement = {
                ...placement,
                channel_total_budget_in_pmds: channelTotalBudgetInPmds,
                channel_total_budget_in_pmds_local: channelTotalBudgetInPmdsLocal,
                nestedData: placement.nestedData.map(nestedPlacement => {
                    if (allMatchingChannels) {
                        let publishers = [];
                        allMatchingChannels.forEach(channel => {
                            let matchingPublisher = channel.publishers?.find(p => p.publisher_name?.toLowerCase() === nestedPlacement.placement_taxonomy?.toLowerCase());
                            if (matchingPublisher) {
                                publishers.push(matchingPublisher);
                            }
                        });
                        let publisherTotalBudgetInPmds = publishers.length > 0 ? publishers.reduce((acc, curr) => acc + (curr.publisher_budget_in_pmds || 0), 0) : 0;
                        let publisherTotalBudgetInPmdsLocal = publishers.length > 0 ? publishers.reduce((acc, curr) => acc + (curr.publisher_budget_in_pmds_local || 0), 0) : 0;
                        return {
                            ...nestedPlacement,
                            publisher_budget_in_pmds: publisherTotalBudgetInPmds,
                            publisher_budget_in_pmds_local: publisherTotalBudgetInPmdsLocal
                        };
                    } else {
                        return {
                            ...nestedPlacement,
                            publisher_budget_in_pmds: '',
                            publisher_budget_in_pmds_local: ''
                        };
                    }
                })
            };
            return mergedPlacement;
        });

        return output;
    }

    loadVendorMapping = (campaignObj) => {
        let publisherList = (campaignObj?.partners || []).join(',');
        let sourceSystem = this.props.systemParametersData?.data?.buyingSystem || '';
        VendorMappingService.getPlannedMappings(publisherList, sourceSystem)
            .then((vendorMappings) => {
                if (vendorMappings?.data?.length) {
                    for (let i = 0; i < vendorMappings.data.length; i++) {
                        // keeping old code for future reference
                        // if (!this.vendorMappingMap[vendorMappings.data[i]?.mediaOcean?.toUpperCase()]) {
                        //     this.vendorMappingMap[vendorMappings.data[i].mediaOcean?.toUpperCase()] = vendorMappings.data[i];
                        // }
                        this.vendorMappingMap[vendorMappings.data[i]?.planned_value] = vendorMappings.data[i]?.mapped_publishers.map(publisher => {
                            return {
                                code: publisher.supplier_code?.toString(),
                                name: publisher.supplier_code + ' ' + publisher.platform_value
                            }
                        });
                    }
                    this.loadPlacements(true);
                } else {
                    this.loadPlacements(true);
                }
                this.vendorMappingMap['sourceSystem'] = sourceSystem;
            }, (error) => {
                this.loadPlacements(true);
                const errorMessage = error?.response?.data?.error?.message || error?.errorMessage;
                AlertMessagesService.showMultipleAlerts([{
                    type: 'danger',
                    retryBtnText: 'Retry',
                    retryBtnEvent: () => this.loadPlacements(true),
                    title: 'Vendor mapping API cannot be loaded',
                    bodyMsgArr: errorMessage ? [html`<b>Error description</b>:<br/>${errorMessage}`] : [],
                }]);
            });
    }

    // on cell click for placement name and parameter code
    onCellClicked = (event) => {
        let data = event?.data;
        // let isChildColumn = this.parameterChildCoulmnName?.filter(val=> val === event?.colDef?.field)?.length;
         if ((event?.colDef?.field === "placement_taxonomy" || event?.colDef?.field === "parameter_code") && !['Channel', 'Publisher'].includes(event?.data?.title)) {
            // Analytics: inline edit open
            trackEvent('Inline edit opened', { access_from: 'Placements Page', campaignId : this.state?.campaignObj?.id, field: event?.colDef?.field, placementId: data?.id });
            this.getPlacementsName(event?.data,event?.colDef?.field);
        }else if(event?.colDef?.field === "mediaocean_status" ){
            if(this.checkStatusType(data)){
                // Analytics: sync error modal open
                trackEvent('Sync Error modal opened', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, placementId: data?.id, status: data?.mediaocean_status });
                this.onSyncErrorModalOpen(data)
            }
        }
    };
    // copy feature code
    handleBulkCopy = (event) => {
        // Analytics: bulk copy click
        trackEvent('Bulk copy initiated', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, itemCount: this.state.selectedItems?.length || 0 });
        const payload = {
            "placement-ids": this.state.selectedItems.map(item => {
                let taxonomyData = PlacementUtils.getDialogTaxonomyData(this.state, PlacementUtils.mapPackageType(item.package_type, item.placement_type, item.parent_type),this.placementTaxonomyMap, this.allTaxonomyData);
                return {
                    placement_id: item.id,
                    taxonomy_client_id: taxonomyData.clientid,
                    taxonomy_schema_id: taxonomyData.id
                };
            }),
            "createdBy": this.props.omniProfile.email,
            "taxonomies": this.taxonomyStrings,
            "is_currency_local": this.props.systemParametersData.data.isCurrencyLocal,
            "campaign_netmedia": this.state.campaignObj?.campaign_total_budget,
            "campaign_netmedia_local": this.state.campaignObj?.campaign_total_budget_local,
        }
        this.props.setSpinnerCount(1);
        let campaignId = encodeURIComponent(this.state.campaignObj.id);
        PlacementDetailsTableService.bulkPlacementsCopy(campaignId, payload, this.props?.versionOneApi).then((response) => {
            trackEvent(`Placements are copied successfully (bulk).`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id });
            PlacementDetailsTableService.onPlacementCopySuccess(response, this.resetStateAndSendAlerts);
        }, (error) => {
            if (error?.data?.result?.length) {
                PlacementDetailsTableService.onPlacementCopyFailure(error, this.resetSelctedItems)
            } else {
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.handleBulkCopy, title: 'Unable to copy the placements', bodyMsgArr: [] }]);
                // this.setState({ selectedItems: [] });
            }
            trackEvent(`Placements copy failed (bulk).`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, error: error?.response?.data || error });
        }).finally(() => {
            this.props.setSpinnerCount(0);
        })
    }

    handleInlineCopy = (props) => {
        // Analytics: inline copy click
        trackEvent('Inline copy invoked', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, placementId: props?.data?.id });
        const payload = props?.data;
        const parentId = payload?.id;
        const selectedItems = [];

        // Add parent campaign
        this.state.placementsFromApi.forEach((item) => {
            if (item?.id?.toString() === parentId?.toString()) {
                selectedItems.push(item);
            }
        });

        const processChildItems = (childItems) => {
            childItems.forEach((childItem, index) => {
                // Add nested child campaigns
                this.state.placementsFromApi.forEach((item) => {
                    if (item?.parent_id?.toString() === childItem?.id?.toString()) {
                        selectedItems.push(item);
                    }
                });

                if (childItems.length === index + 1) {
                    this.copyPlacements(selectedItems, props);
                }
            });
        };

        if (payload?.package_type === "ProgrammaticPackage") {
            // Add child campaigns
            const childItems = this.state.placementsFromApi.filter(
                (item) => item?.parent_id?.toString() === parentId?.toString()
            );
            selectedItems.push(...childItems);
            processChildItems(childItems);
        } else if (
            payload?.package_type === "Package" ||
            payload?.package_type === "DSP" ||
            payload?.package_type.includes("Package")
        ) {
            // Add child campaigns
            this.state.placementsFromApi.forEach((item, itemIndex) => {
                if (item?.parent_id?.toString() === parentId?.toString()) {
                    selectedItems.push(item);
                }
                if (itemIndex + 1 === this.state.placementsFromApi.length) {
                    this.copyPlacements(selectedItems, props);
                }
            });
        } else {
            this.copyPlacements(selectedItems, props);
        }
    };

    copyPlacements = (selectedItems, props) => {
        // Analytics: copy initiated
        trackEvent('Copy initiated', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, itemCount: selectedItems?.length || 0 });
        const payload = {
            "placement-ids": selectedItems.map((item) => {
                let taxonomyData = PlacementUtils.getDialogTaxonomyData(this.state, PlacementUtils.mapPackageType(item.package_type, item.placement_type, item.parent_type),this.placementTaxonomyMap, this.allTaxonomyData);
                return {
                    placement_id: item.id,
                    taxonomy_client_id: taxonomyData.clientid,
                    taxonomy_schema_id: taxonomyData.id
                };
            }),
            "createdBy": this.props.omniProfile.email,
            "taxonomies": this.taxonomyStrings,
            "is_currency_local": this.props.systemParametersData.data.isCurrencyLocal,
            "campaign_netmedia": this.state.campaignObj?.campaign_total_budget,
            "campaign_netmedia_local": this.state.campaignObj?.campaign_total_budget_local,
        };

        this.props.setSpinnerCount(1);
        const campaignId = encodeURIComponent(this.state.campaignObj.id);

        PlacementDetailsTableService.bulkPlacementsCopy(campaignId, payload, this.props?.versionOneApi)
            .then((response) => {
                trackEvent(`Placements are copied successfully.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id });
                PlacementDetailsTableService.onPlacementCopySuccess(response, this.resetStateAndSendAlerts);
            })
            .catch((error) => {
                if (error?.data?.result?.length) {
                    PlacementDetailsTableService.onPlacementCopyFailure(error, this.resetSelctedItems);
                } else {
                    AlertMessagesService.showMultipleAlerts([
                        {
                            type: "danger",
                            retryBtnText: "Retry",
                            retryBtnEvent: () => this.handleInlineCopy(props),
                            title: "Unable to copy the placements",
                            bodyMsgArr: [],
                        },
                    ]);
                }
                trackEvent(`Placements copy failed.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, error: error?.response?.data || error });
            })
            .finally(() => {
                this.props.setSpinnerCount(0);
            });
    };
    
    /*
     * Archive or restore the selected placements based on the current tab (archiveTab state).
     * @param {Array} selectedItems - The list of placements to be archived or restored.
    */
    archivePlanit = (selectedItems) => {
        this.props.setSpinnerCount(1);
        let plannedIds = selectedItems.map(item => item.tier_id);
        const payload = { "ids": [], "isDelete": !this.state.archiveTab, "media_ocean_ids":[], "plannedIds": plannedIds };
        const campaignId = encodeURIComponent(this.state.campaignObj.id);
         PlacementDetailsTableService.deletePlacement(campaignId, payload, this.props?.versionOneApi).then((response) => {
            AlertMessagesService.showSuccessToastMessage(this.state.archiveTab ? 'Selected Planit Placement(s) restored successfully' : 'Selected Planit Placement(s) archived successfully');
            this.loadPlacements();
        }).catch(error => {
            this.handleArchiveError(error, selectedItems, []);
        });
    }

    //archive code
    confirmArchiveMsg = (isBulk, data, index) => {
        this.lineItemParentMap = {};
        this.createMapForLineItems();
        let isPlacementSentItemsPresent = false;
        const archiveText = this.state.archiveTab ? 'restore' : 'archive';
        const archiveCapText = this.state.archiveTab ? 'Restore' : 'Archive';

        if(!isBulk && data?.data_source.toLowerCase() === 'planit' || (this.state.selectedItems?.length && this.state.selectedItems[0]?.data_source.toLowerCase() === 'planit')){
            om2confirm(`This action will ${archiveText} the selected placement(s). Are you sure you want to do this?`, {
                callback: () => this.archivePlanit(!isBulk && data ? [data] : this.state.selectedItems),
                type: "warning",
                title: `${archiveCapText} planit placement(s)`,
            });
            return;
        }

        // check if the selected items are sent to media ocean
        if (this.state.selectedItems?.length) {
            isPlacementSentItemsPresent = !!this.state.selectedItems.filter(item => item.mediaocean_status === 'Placement Sent')?.length
        } else if (Object.keys(data)?.length) {
            isPlacementSentItemsPresent = data?.mediaocean_status === 'Placement Sent'
        }

        if (data) {
            data["updated_by"] = this.props && this.props.omniProfile && this.props.omniProfile["email"];

            if (this.lineItemParentMap[data.parent_id]?.count === 1) {
                const confirmMessage = isPlacementSentItemsPresent
                    ? `This line is already sent to media ocean and is the only line item for this Programmatic package, On ${archiveText}, it will ${archiveText} the parent package also. Are you sure you want to ${archive} it?`
                    : `This is the only line item for this Programmatic package, On ${archiveText}, it will ${archiveText} the parent package also. Are you sure you want to ${archiveText} it?`;

                om2confirm(confirmMessage, {
                    callback: () => this.archivePlacementService(false, this.lineItemParentMap[data.parent_id].parent),
                    type: "warning",
                    title: `${archiveCapText} Line Item With Package`,
                });
            } else {
                const confirmMessage = isPlacementSentItemsPresent
                    ? `Are you sure you want to ${archiveText} this Package(s)/Placements(s) it has already been sent to the Buying System`
                    : `This action will ${archiveText} the selected placements. Are you sure you want to do this?`;

                om2confirm(confirmMessage, {
                    callback: () => isBulk ? this.archivePlacementService(isBulk) : this.archivePlacementService(isBulk, data, index),
                    type: "warning",
                    title: `${archiveCapText} Placements`,
                });
            }
        } else { // bulk archive
            let isSingleLineItem = false;
            let parentItems = [];
            let singlelineItems = [];
            // Check if the selected line item is the only line item in the package.
            this.state.selectedItems.forEach((item) => {
                if (this.lineItemParentMap[item.parent_id]?.count === 1) {
                    parentItems.push(this.lineItemParentMap[Number(item.parent_id)].parent);
                    singlelineItems.push(item);
                    isSingleLineItem = true;
                }
            })
            // If the selected line item is the only line item in the package, it confirms the archive action for the parent package also.
            if (isSingleLineItem) {
                let unselectedParentIds = parentItems.filter(
                    (parent) =>
                        !this.state.selectedItems.some((item) => item.id === parent.id)
                );
                let listWithNoSingleLineItem = this.state.selectedItems.filter(
                    (item) =>
                        !singlelineItems.some((singleItem) => singleItem.id === item.id)
                );
                let archiveItems = listWithNoSingleLineItem.concat(unselectedParentIds);

                const confirmMessage = isPlacementSentItemsPresent
                    ? `Some selected Line items are already sent to media ocean and the only line item for their respective Programmatic package, On ${archiveText}, it will ${archiveText} the respective parent package also. Are you sure you want to ${archiveText} them?`
                    : `Some selected Line items are the only line item for their respective Programmatic package, On ${archiveText}, it will ${archiveText} the respective parent package also. Are you sure you want to ${archiveText} them?`;

                om2confirm(confirmMessage, {
                    callback: () => this.archivePlacementService(true, archiveItems),
                    type: "warning",
                    title: `${archiveCapText} Selected Placements/Line Items With Package`,
                });
            } else {
                const confirmMessage = isPlacementSentItemsPresent
                    ? `The selected placements are already sent to media ocean and this action will ${archiveText} the selected placements. Are you sure you want to do this?`
                    : `This action will ${archiveText} the selected placements. Are you sure you want to do this?`;

                om2confirm(confirmMessage, {
                    callback: () => this.archivePlacementService(true),
                    type: "warning",
                    title: `${archiveCapText} Selected Placements`,
                });
            }
        }
    }

    /**
     * Validates and generates a payload for inline changes in the AG Grid.
     * This function handles updates for various columns such as 'Rate', 'Unit Amount', 'Planned Cost',
     * 'ad_server_fee', and 'ad_verification_fee'. It validates the new value, ensures it is different
     * from the current value, and constructs a payload for further processing.
     *
     * @param {string} column - The name of the column being updated (e.g., 'Rate', 'Unit Amount', 'Planned Cost').
     * @param {Object} event - The event object containing details about the change.
     * @param {Object} value - The current row data for the placement being updated.
     * @param {number|string} currentValue - The current value of the column before the update.
     * @returns {Object|null} - The payload object containing the updated data, or null if no changes are made.
     */
    validateAndGetPayloadForInlineChanges = (column, event, value, currentValue) => {
        const isLocal = this.props.systemParametersData.data.isLocal;
        const currency = isLocal && this.state.campaignObj.local_currency_name ? this.state.campaignObj.local_currency_name : 'USD';

        let payload = null
        const numberRegex = /[0-9,]+(\.[0-9]+)?/;
        switch (column) {
            case 'Rate':
            case 'Unit Amount':
            case 'Planned Cost': {
                let updatedValue = event.target.value;
                updatedValue = Number(updatedValue.match(numberRegex)[0].replace(/,/g, ''));
                // Show toast message to indicate units have been rounded off to the nearest whole number
                if (isNaN(updatedValue)) {

                    AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.validateAndGetPayloadForInlineChanges(column, event, value), title: 'Unable to update the rate', bodyMsgArr: [] }]);
                    event.target.value = column === 'Rate' ? PlacementDetailsTableService.getRate(value, isLocal, currency) : currentValue;
                    return;
                }
                if (Number(updatedValue) === Number(currentValue)) {
                    return; // return if nothing is changed
                }
                if (column === 'Unit Amount' && (updatedValue?.toString().indexOf(".") > -1)) {
                    AlertMessagesService.showErrorToastMessage(`Units can not be a decimal. We have rounded down to the nearest whole number`);
                    event.target.value = currentValue;
                    return;
                }
                payload = {
                    placementCost: PlacementUtils.getCost(column, value, updatedValue, isLocal, currency),
                    client_id: value.client_id
                };
            }
                break;

            case "ad_server_fee":
                let updatedAdServerValue = event?.target?.value;

                if (value?.ad_server_fee === null && updatedAdServerValue.trim() === '') {
                    return;
                }

                if (updatedAdServerValue.trim() !== '$' && updatedAdServerValue.trim() !== '') {
                    if (updatedAdServerValue.trim().includes('$')) {
                        updatedAdServerValue = updatedAdServerValue.replace('$', '');
                    }
                    updatedAdServerValue = Number(updatedAdServerValue.match(numberRegex) && updatedAdServerValue.match(numberRegex)[0].replace(/,/g, ''));
                    if (updatedAdServerValue === value?.ad_server_fee) {
                        return;
                    }

                    if (isNaN(updatedAdServerValue)) {
                        AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.validateAndGetPayloadForInlineChanges(column, event, value), title: 'Unable to update the ad server fee', bodyMsgArr: [] }]);
                        event.target.value = PlacementDetailsTableService.getAdServerFee(value);
                        return;
                    }
                    if (updatedAdServerValue === PlacementDetailsTableService.getAdServerFee(value)) {
                        return; // return if nothing is changed
                    }

                    payload = { ad_server_fee: updatedAdServerValue, is_ad_server_fee_override: true }
                } else {
                    event.target.value = null;
                    payload = { ad_server_fee: null, is_ad_server_fee_override: false }
                }
                break;

            case "ad_verification_fee":
                let updatedAdVerificationValue = event?.target?.value;

                if (value?.ad_verification_fee === null && updatedAdVerificationValue.trim() === '') {
                    return;
                }

                if (updatedAdVerificationValue.trim() !== '$' && updatedAdVerificationValue.trim() !== '') {
                    if (updatedAdVerificationValue.trim().includes('$')) {
                        updatedAdVerificationValue = updatedAdVerificationValue.replace('$', '');
                    }
                    updatedAdVerificationValue = Number(updatedAdVerificationValue.match(numberRegex) && updatedAdVerificationValue.match(numberRegex)[0].replace(/,/g, ''));
                    if (updatedAdVerificationValue === value?.ad_verification_fee) {
                        return;
                    }

                    if (isNaN(updatedAdVerificationValue)) {
                        AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.validateAndGetPayloadForInlineChanges(column, event, value), title: 'Unable to update the ad verification fee', bodyMsgArr: [] }]);
                        event.target.value = PlacementDetailsTableService.getAdServerFee(value);
                        return;
                    }
                    if (updatedAdVerificationValue === PlacementDetailsTableService.getAdServerFee(value)) {
                        return; // return if nothing is changed
                    }
                    payload = { ad_verification_fee: updatedAdVerificationValue, is_ad_verification_fee_override: true }
                } else {
                    event.target.value = null;
                    payload = { ad_verification_fee: null, is_ad_verification_fee_override: false }
                }
                break;

            default:
                break;
        }
        payload = {
            ...payload,
            "is_currency_local": this.props.systemParametersData.data.isCurrencyLocal,
            "campaign_netmedia": this.state.campaignObj?.campaign_total_budget,
            "campaign_netmedia_local": this.state.campaignObj?.campaign_total_budget_local,
        }
        return payload
    }

    /**
     * Handles cell value changes in the AG Grid.
     * This function validates the updated value for specific columns and triggers the appropriate update logic.
     * It supports columns such as 'planned_cost', 'rate', 'unit_amount', 'flight_start', and 'flight_end'.
     *
     * @param {Object} event - The event object containing details about the cell value change.
     * @param {Object} event.colDef - The column definition of the cell being edited.
     * @param {string} event.colDef.field - The field name of the column being edited.
     * @param {any} event.newValue - The new value entered by the user.
     * @param {any} event.oldValue - The previous value of the cell before the edit.
     * @param {Object} event.data - The row data for the placement being updated.
     * @returns {void}
     */
    handleCellValueChanged = (event) => {
        let payload = {};
        let type = '';
        let isCascading = false;
        if (["planned_cost", "planned_cost_local"].includes(event?.colDef?.field)) {
            const updatedValue = event?.newValue;
            type = 'planned cost';
            if (updatedValue !== event?.oldValue) {
                payload = this.validateAndGetPayloadForInlineChanges("Planned Cost", { target: { value: updatedValue?.toString() } }, event?.data, event?.oldValue);
                trackEvent('Inline update attempted for planned cost', { access_from: 'Placements Page', placementId: event?.data?.id });
                // this.updatePlacementDataInline(payload, event?.data, 'planned cost');
            }
        } else if (["rate", "rate_local"].includes(event?.colDef?.field)) {
            const updatedValue = event?.newValue;
            type = 'rate';
            if (updatedValue !== event?.oldValue) {
                payload = this.validateAndGetPayloadForInlineChanges("Rate", { target: { value: updatedValue?.toString() } }, event?.data, event?.oldValue);
                trackEvent('Inline update attempted for rate', { access_from: 'Placements Page', placementId: event?.data?.id });
                // this.updatePlacementDataInline(payload, event?.data, 'rate');
                const rowNode = this.gridRef?.current?.api?.getDisplayedRowAtIndex(event?.rowIndex);
                            // pick 'c' column
                this.gridRef?.current?.api?.flashCells({ rowNodes: [rowNode], columns: ["rate"] });
            }
        } else if (["unit_amount"].includes(event?.colDef?.field)) {
            const updatedValue = event?.newValue;
            type = 'unit amount';
            if (updatedValue !== event?.oldValue) {
                payload = this.validateAndGetPayloadForInlineChanges("Unit Amount", { target: { value: updatedValue?.toString() } }, event?.data, event?.oldValue);
                trackEvent('Inline update attempted for unit amount', { access_from: 'Placements Page', placementId: event?.data?.id });
                // this.updatePlacementDataInline(payload, event?.data, 'unit amount');
            }
        } else if (["flight_start"].includes(event?.colDef?.field)) {
            const updatedValueDateString = PlacementUtils.getDateString(event?.newValue);
            const oldValueDateString = PlacementUtils.getDateString(event?.oldValue);
            type = 'start date';
            if (event?.newValue && updatedValueDateString !== oldValueDateString) {
                payload = {
                    "flightStart": updatedValueDateString,
                    "is_currency_local": this.props.systemParametersData.data.isCurrencyLocal,
                    "campaign_netmedia": this.state.campaignObj?.campaign_total_budget,
                    "campaign_netmedia_local": this.state.campaignObj?.campaign_total_budget_local,
                }
                trackEvent('Inline update attempted for start date', { access_from: 'Placements Page', placementId: event?.data?.id });
                // this.updatePlacementDataInline(payload, event?.data, 'start date');
            }
        } else if (["flight_end"].includes(event?.colDef?.field)) {
            const updatedValueDateString = PlacementUtils.getDateString(event?.newValue);
            const oldValueDateString = PlacementUtils.getDateString(event?.oldValue);
            type = 'end date';
            if (event?.newValue && updatedValueDateString !== oldValueDateString) {
                payload = {
                    "flightEnd": updatedValueDateString,
                    "is_currency_local": this.props.systemParametersData.data.isCurrencyLocal,
                    "campaign_netmedia": this.state.campaignObj?.campaign_total_budget,
                    "campaign_netmedia_local": this.state.campaignObj?.campaign_total_budget_local,
                }
                trackEvent('Inline update attempted for end date', { access_from: 'Placements Page', placementId: event?.data?.id });
                // this.updatePlacementDataInline(payload, event?.data, 'end date');
            }
        // updates the taxonomy values & payload on change of taxonomy fields value
        } else if (["TAXONOMY"].includes(event?.colDef?.parentField)) {
            const updatedValue = event?.newValue;
            if (updatedValue.toLowerCase() !== event?.oldValue.toLowerCase()) {
                let {taxonomySelectedValueJson} = PlacementUtils.createNames(event.data,event.data.taxonomyFieldsData);
                let updatedPlacementName = PlacementDetailsTableService.createStringFromJson(taxonomySelectedValueJson,'code',false,event.data.taxonomyFieldsData?.fields);
                let updatedPlacementDisplayName = PlacementDetailsTableService.createStringFromJson(taxonomySelectedValueJson,'name',false,event.data.taxonomyFieldsData?.fields);
                let updatedOverridePlacementName = PlacementDetailsTableService.createStringFromJson(taxonomySelectedValueJson,'code',true,event.data.taxonomyFieldsData?.fields);
                let updatedOverridePlacementDisplayName = PlacementDetailsTableService.createStringFromJson(taxonomySelectedValueJson,'name',true,event.data.taxonomyFieldsData?.fields);
                let updatedSchema = taxonomySelectedValueJson.filter((item) => !item.hidden).map(item => item.field_name).join('|');

                payload = {
                    "placementTaxonomy": updatedPlacementName,
                    "placementTaxonomyDisplay": updatedPlacementDisplayName,
                    "overridenPlacementTaxonomy": updatedOverridePlacementName,
                    "overridenPlacementDisplayTaxonomy": updatedOverridePlacementDisplayName,
                    "is_currency_local": this.props.systemParametersData.data.isCurrencyLocal,
                    "campaign_netmedia": this.state.campaignObj?.campaign_total_budget,
                    "campaign_netmedia_local": this.state.campaignObj?.campaign_total_budget_local,
                    "taxonomy": taxonomySelectedValueJson,
                    "taxonomySchema": updatedSchema,
                    "overrideTaxonomy": false
                }
            } else payload = null;
        } else if(['Parameter'].includes(event?.colDef?.parentField)) {
            //update inline placement on ag grid table
            const updatedValue = event?.newValue;
            type = 'parameter';
            if (updatedValue !== event?.oldValue) {
                
                let parameterValues = event?.data?.parameterCodeSplitValues|| [];
                const index = parameterValues.findIndex(item => item.name === event?.colDef?.field);
                parameterValues[index].value = updatedValue;
                let parameterCode = parameterValues.map(item => item.value);
                const schema = event?.data?.parameter_code_taxonomy_schema?.split('|');
                let parameterCodeString = PlacementDetailsTableService.createStringFromArray(this.allTaxonomyData.defaultTaxonomyData?.fields,parameterCode,schema);
                let name = event?.colDef?.field;
                payload = {
                    "is_currency_local": this.props.systemParametersData.data.isCurrencyLocal,
                    "campaign_netmedia": this.state.campaignObj?.campaign_total_budget,
                    "campaign_netmedia_local": this.state.campaignObj?.campaign_total_budget_local,
                    parameterCode: parameterCodeString,
                    parameterCodeTaxonomySchema: event?.data?.parameter_code_taxonomy_schema,
                    parameterCodeTaxonomyDisplay: parameterCodeString
                }
                payload["parameter_code"] = parameterCodeString;
                //placement type alway required for define which type of placement is belong
                payload["placementType"] = event?.data['Buy Type']; 
                 if(name === 'Buy Category'){
                    payload["buyCategory"] = updatedValue;
                }else if(name === 'Site'){
                    payload["provider"] = updatedValue;
                    payload["site"] = updatedValue;
                }else if(name === 'Ad Verification'){
                    payload['ad_verification'] = updatedValue;
                }else if(name === 'Ad Server'){
                    payload['ad_server'] = updatedValue;
                }else if(name === 'Dimension/Positions'){
                    payload['dimension_positions'] = updatedValue;
                }
                
            }
            //to handle cascading for parameter code fields
            let defaultTaxonomyData = {...this.allTaxonomyData?.defaultTaxonomyData}
            let field = this.allTaxonomyData?.defaultTaxonomyData.fields.find(val=> val.alias == event.colDef.field || val.name ==event.colDef.field);
            let selectedField = field.fielditems.filter(val=> val.code == event.value)
            let updatedDefualtTaxonomy = PlacementUtils.updateDefaultTaxonomyFields(defaultTaxonomyData,selectedField);
            let columnIndex = columnDefs.findIndex(val=> val.headerName == "PARAMETER");
            let childField = []
            updatedDefualtTaxonomy.forEach((field,index)=>{
                if(columnDefs[columnIndex]?.headerName == 'PARAMETER' && defaultTaxonomyData?.fields?.length === columnDefs[columnIndex]?.children?.length){
                    let newField = [];
                    if(field?.fielditems?.length){
                        newField = {id : field.id , option : [...field.fielditems],rowIndex : event?.rowIndex};
                    }
                    childField.push(newField);
                    if(field?.isCascaded){
                        isCascading = field?.isCascaded;
                        let name = field.name || field.alias;
                        const rowNode = this.gridRef?.current?.api.getDisplayedRowAtIndex(event?.rowIndex);

                        this.gridRef?.current?.api.flashCells({ rowNodes: [rowNode], columns: [name] });
                    }
                }
            });

            this.setState({childField:childField});
            
        }

        if(!isCascading && event.source === 'edit'){
            this.updatePlacementDataInline(payload, event?.data, type);
        }
        // if(event.source == 'edit'){
        // }else 
        if(event.source !== 'edit'){
            this.inlineType = type;
            this.inlinePayload.push({...payload, id: event?.data?.id,isCascading:isCascading});
        }            
    }

    /**
     * Handles the end of a fill operation in the AG Grid.
     * calling API to update multiple placements with the collected payloads.
     * @param {Object} event - The event object containing information about the fill operation.
     */
    onFillEnd = (retryPayload) => {
        let payload = this.inlinePayload?.length ? this.inlinePayload : retryPayload;
        // call API to update placements with this.inlinePayload
        if (payload?.length && !payload[0]?.isCascading) {
            // let payload = this.inlinePayload;
            this.props.setSpinnerCount(1);
            let campaignId = encodeURIComponent(this.state.campaignObj.id);
            trackEvent('Placement update requested', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, updateType: this.inlineType });
            CreatePlacementOrPackageService.updatePlacements(campaignId, payload, this.props?.versionOneApi).then(res => {
                this.props.setSpinnerCount(0);
                AlertMessagesService.showSuccessToastMessage(`Package/Placement ${this.inlineType} is updated`);
                trackEvent('Placement updated', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, updateType: this.inlineType });
                this.inlinePayload = [];
                this.inlineType = '';
                this.loadPlacements(false, true);
            }).catch(err => {
                this.inlinePayload = []
                this.inlineType = '';
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.onFillEnd(payload), title: `Placement update API cannot be loaded`, bodyMsgArr: [] }]);
                this.props.setSpinnerCount(0);
                trackEvent('Placement update failed', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, updateType: this.inlineType, error: err?.response?.data || err });
            });
        }else{
            this.inlinePayload = []
            this.inlineType = '';
        }
    }

    /**
     * Updates placement data inline by sending a payload to the backend API.
     * This function is triggered when a cell value is changed in the AG Grid.
     * It handles API calls to update the placement data and reloads the placements upon success.
     *
     * @param {Object} payload - The data to be sent to the backend for updating the placement.
     * @param {Object} value - The current row data for the placement being updated.
     * @returns {void}
     */
    updatePlacementDataInline = (payload, value, columnName) => {
        if (payload) {
            payload.updated_by = this.props?.omniProfile?.email
            this.props.setSpinnerCount(1);
            let campaignId = encodeURIComponent(this.state.campaignObj.id);
            trackEvent('Placement column update requested', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, columnName, placementId: value?.id });
            CreatePlacementOrPackageService.updatePlacementOrPackage(campaignId, value?.id, payload, this.props?.versionOneApi).then(res => {
                this.props.setSpinnerCount(0);
                AlertMessagesService.showSuccessToastMessage(`Package/Placement ${columnName} is updated`);
                trackEvent('Placement column updated', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, columnName, placementId: value?.id });
                this.loadPlacements(false, true);
            }).catch(err => {
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.updatePlacementDataInline(payload, value, columnName), title: `Placement update API cannot be loaded`, bodyMsgArr: [] }]);
                this.props.setSpinnerCount(0);
                trackEvent('Placement column update failed', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, columnName, placementId: value?.id, error: err?.response?.data || err });
            });
        }
    }

    onSelectionChanged = (event) => {
        this.setState({ selectedItems: this.getSelectedRows() }, () => {
            this.updateSelectedItemsInState()
        });
    }


    createMapForLineItems = () => {
        for (let i = 0; i < this.state.placementsFromApi.length; i++) {
            if (!this.lineItemParentMap[this.state.placementsFromApi[i].id]) {
                this.lineItemParentMap[this.state.placementsFromApi[i].id] = { parent: this.state.placementsFromApi[i], lineItems: [], count: 0 };
            }
            if (this.state.placementsFromApi[i].parent_id && this.state.placementsFromApi[i].package_type === 'DSP') {
                let parentId = Number(this.state.placementsFromApi[i].parent_id);
                if (!this.lineItemParentMap[parentId]) {
                    this.lineItemParentMap[parentId] = { parent: this.state.placementsFromApi[i], lineItems: [], count: 0 };
                }
                this.lineItemParentMap[parentId].lineItems.push(this.state.placementsFromApi[i]);
                this.lineItemParentMap[parentId].count++;
            }
        }
    }

    archivePlacementService = (isBulk, placementData, rowIndex) => {
        // Clear smart suggestions cache before archive/restore
        this.smartSuggestionsCache = {}; // Clear component-level cache
        
        // this.setState({ spinnerCount: 1 });
        this.props.setSpinnerCount(1);
        let responseMessages = [];
        const archiveAction = this.state.archiveTab ? 'restored' : 'archived';
        let campaignId = encodeURIComponent(this.state.campaignObj.id);

        // Analytics: archive/restore requested
        const itemCount = isBulk ? (placementData?.length || this.state.selectedItems?.length || 0) : 1;
        trackEvent('Archive action requested', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, action: this.state.archiveTab ? 'restore' : 'archive', itemCount });

        if (!isBulk) {
            this.handleSingleArchive(placementData, campaignId, archiveAction, responseMessages, rowIndex, isBulk);
        } else {
            this.handleBulkArchive(placementData, campaignId, archiveAction, responseMessages, rowIndex, isBulk);
        }
    }

    handleSingleArchive = (placementData, campaignId, archiveAction, responseMessages, rowIndex, isBulk) => {
        if (placementData.id) {
            let placementsWithChildren = this.getChildPlacements(placementData);
            const placementIds = [];
            const placementMOIds = [];

            if (!Array.isArray(placementsWithChildren)) {
                placementsWithChildren = [placementsWithChildren];
            }

            placementsWithChildren.forEach((placement) => { 
                placementIds.push(placement?.id);
                if (placement?.mediaocean_id) {
                    placementMOIds.push(placement?.mediaocean_id);
                }
            });

            const payload = { "ids": placementIds, "isDelete": !this.state.archiveTab,"media_ocean_ids":placementMOIds };

            PlacementDetailsTableService.deletePlacement(campaignId, payload, this.props?.versionOneApi).then((response) => {
                this.handleArchiveResponse(response, placementsWithChildren, archiveAction, responseMessages, rowIndex, isBulk, placementData);
            }).catch(error => {
                this.handleArchiveError(error, placementsWithChildren, responseMessages);
            });
        }
    }

    handleBulkArchive = (placementData, campaignId, archiveAction, responseMessages, rowIndex, isBulk) => {
        let placementItems = [];
        let placementIds = [];
        let placementMOIds = [];

        if (placementData && placementData.length > 0) {
            placementItems = placementData;
        } else {
            placementItems = this.state.selectedItems;
        }

        placementItems.forEach((placement) => {
            const childPlacements = this.getChildPlacements(placement);
            if (Array.isArray(childPlacements)) {
                childPlacements.forEach((childPlacement) => {
                    if (!placementIds.includes(childPlacement?.id)) {
                        placementIds.push(childPlacement?.id);
                    }
                    if (!placementMOIds.includes(childPlacement?.mediaocean_id) && childPlacement?.mediaocean_id) {
                        placementMOIds.push(childPlacement?.mediaocean_id);
                    }
                });
            } else {
                if (!placementIds.includes(childPlacements?.id)) {
                    placementIds.push(childPlacements?.id);
                }
                if (!placementMOIds.includes(childPlacements?.mediaocean_id) && childPlacements?.mediaocean_id) {
                    placementMOIds.push(childPlacements?.mediaocean_id);
                }
            }
        });

        const payload = { "ids": placementIds, "isDelete": !this.state.archiveTab,"media_ocean_ids":placementMOIds };
        PlacementDetailsTableService.deletePlacement(campaignId, payload, this.props?.versionOneApi).then((response) => {
            this.handleArchiveResponse(response, placementItems, archiveAction, responseMessages, rowIndex, isBulk, placementData);
        }).catch(error => {
            this.handleArchiveError(error, placementItems, responseMessages);
        });
    }

    getChildPlacements = (payload) => {
        if (payload.package_type === "ProgrammaticPackage" || payload.package_type === "Package") {
            let childPlacmentsArray = this.state.placementsFromApi.reduce((acc, item) => {
                if (item?.parent_id === payload?.id?.toString()) {
                    acc.push(item);
                    this.state.placementsFromApi.forEach((obj) => {
                        if (obj?.parent_id === item?.id?.toString()) {
                            acc.push(obj);
                            this.state.placementsFromApi.forEach((innrObj) => {
                                if (innrObj?.parent_id === obj?.id?.toString()) {
                                    acc.push(innrObj);
                                    this.state.placementsFromApi.forEach((lastObj) => {
                                        if (lastObj?.parent_id === innrObj?.id?.toString()) {
                                            acc.push(lastObj);
                                        }
                                    });
                                }
                            });
                        }
                    });
                }
                return acc;
            }, []);
            childPlacmentsArray.push(payload);
            return childPlacmentsArray;
        } else {
            return payload;
        }
    };

    handleArchiveResponse = (response, placements, archiveAction, responseMessages, rowIndex, isBulk, placementData) => {
        const updateResults = response.data?.updateResults;
        let successCount = 0; let failureCount = 0;
        updateResults.forEach((result, resultIndex) => {
            const placementName = placements.find(item => item.id?.toString() === result.id?.toString())?.placement_taxonomy;
            
            if (result.status === "success") {
                successCount++;
                responseMessages.push({
                    type: "success",
                    title: `Placement is ${archiveAction}`,
                    "bodyMsgArr": [html`Placement name:<br/>${placementName}`]
                });
            } else {
                failureCount++;
                responseMessages.push({
                    type: "danger",
                    retryBtnText: 'Retry',
                    retryBtnEvent: () => this.archivePlacementService(isBulk, placementData || placements, rowIndex),
                    title: `Error in ${this.state.archiveTab ? 'restoring' : 'archiving'} placement`,
                    "bodyMsgArr": [html`Placement name:<br/>${placementName}`, html`${result?.validation}`]
                });
            }

            if (updateResults.length === resultIndex + 1) {
                AlertMessagesService.showMultipleAlerts(responseMessages);
                // this.fetchPlacements(null, true, false, null, null);
                this.props.setSpinnerCount(0);
                this.loadPlacements();
                // Analytics: archive results
                trackEvent('Placements archive action completed', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, action: archiveAction, successCount, failureCount });
            }
        });
    }

    handleArchiveError = (error, placements, responseMessages) => {
        placements.forEach((placement, placementIndex) => {
            responseMessages.push({
                type: "danger",
                title: error.message,
                "bodyMsgArr": [html`Placement name:<br/>${placement?.placement_taxonomy}`, html`Error description:<br/>${error?.response?.data?.error?.message}`]
            });
            if (placementIndex + 1 === placements.length) {
                AlertMessagesService.showMultipleAlerts(responseMessages);
                this.props.setSpinnerCount(0);
            }
        });
        // Analytics: archive failure
        trackEvent('Placements archive action failed', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, action: this.state.archiveTab ? 'restore' : 'archive', error: error?.response?.data || error });
    }
    
    // buying system code
    handleBuyingSystemChange = (type, value) => {
        const selectedItems = this.getSelectedRowsWithFewChildrenSelected();

        if (type === "Approval for Buying System") {
            const placementIds = selectedItems.map(item => item.id);

            if (!placementIds.length && !value) {
                AlertMessagesService.showInfoToastMessage('Please select at least one placement to approve');
                return;
            }

            this.props.setSpinnerCount(1);
            const campaignId = this.state?.campaignObj?.id || this.props?.state?.campaignObj?.id;
            const encodedCampaignId = encodeURIComponent(campaignId);
            const payload = {
                approvedBy: this.props.omniProfile.email,
                isApproved: !value,
                ids: value ? [value.id] : placementIds,
            };

            // Analytics: approval toggle request
            trackEvent('Approval toggle requested', { access_from: 'Placements Page', campaignId, isApproved: payload.isApproved, itemCount: payload.ids?.length || 0 });

            PlacementDetailsTableService.getPlacementsApprovedChunked(encodedCampaignId, payload, { id: campaignId }, this.props?.versionOneApi)
                .then(response => this.handleApprovalResponse(response, value))
                .catch((error) => this.handleApprovalError(type, value, error))
                .finally(() => {
                    this.props.setSpinnerCount(0);
                    this.loadPlacements();
                });
        } else {
            if (!selectedItems.length) {
                AlertMessagesService.showInfoToastMessage('Please select at least one placement to send to Buying System');
                return;
            }

            const alreadySentCount = selectedItems.filter(item => item.mediaocean_status?.toLowerCase() === 'placement sent').length;

            if (alreadySentCount) {
                AlertMessagesService.showWarningToastMessage(`${alreadySentCount} placement(s) are already sent to Media Ocean.`);
            }

            const filteredItems = selectedItems.filter(item => item.mediaocean_status?.toLowerCase() !== 'placement sent');
            if (filteredItems.length) {
                // Analytics: send to MO start
                trackEvent('Send to Buying System initiated', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, itemCount: selectedItems.length, alreadySentCount });
                
                // Validate supplier budgets and campaign status before sending
                this.validateBudgetsAndCampaignStatus(selectedItems, filteredItems);
            }
        }
    };

    /**
     * Validates supplier budgets and campaign status before sending placements to Media Ocean.
     *
     * Steps:
     * 1. Checks if any supplier exceeds their planned budget — shows a warning modal if so.
     * 2. After budget check passes (or user overrides), checks the campaign's mediaocean_status via API.
     *    - If "Campaign Sent" or "Synced": proceeds directly to send placements.
     *    - If not sent: prompts user to optionally send the campaign first.
     *    - If the campaign status API fails: proceeds directly to send placements.
     *
     * @param {Array} selectedItems - All selected items including children
     * @param {Array} filteredItems - Items filtered to exclude already sent placements
     */
    validateBudgetsAndCampaignStatus = (selectedItems, filteredItems) => {
        const isLocal = this.props?.systemParametersData?.data?.isLocal;
        const costField = isLocal ? 'planned_cost_local' : 'planned_cost';
        
        // Filter parent placements only (parent_id is null or undefined)
        const parentPlacements = filteredItems.filter(item => !item.parent_id);
        
        // Calculate total planned cost by supplier
        const supplierTotals = {};
        parentPlacements.forEach(placement => {
            const supplier = placement.Supplier;
            const plannedCost = parseFloat(placement[costField]) || 0;
            
            if (!supplierTotals[supplier]) {
                supplierTotals[supplier] = 0;
            }
            supplierTotals[supplier] += plannedCost;
        });
        
        // Get supplier budgets from campaign channels/publishers
        const channels = this.state.campaignObj?.media_attributes?.channels || [];
        const supplierBudgets = {};
        
        channels.forEach(channel => {
            channel.publishers?.forEach(publisher => {
                const publisherName = publisher.publisher_name;
                const budget = isLocal 
                    ? (publisher.publisher_budget_in_pmds_local || 0)
                    : (publisher.publisher_budget_in_pmds || 0);
                
                if (!supplierBudgets[publisherName]) {
                    supplierBudgets[publisherName] = 0;
                }
                supplierBudgets[publisherName] += budget;
            });
        });
        
        // Check for over-budget suppliers
        const overBudget = [];
        Object.keys(supplierTotals).forEach(supplier => {
            const plannedAmount = supplierTotals[supplier];
            const availableBudget = supplierBudgets[supplier] || 0;
            
            if (plannedAmount > availableBudget) {
                overBudget.push({
                    name: supplier,
                    plannedAmount,
                    availableBudget,
                    overAmount: plannedAmount - availableBudget
                });
            }
        });
        
        if (overBudget.length > 0) {
            // Show budget warning modal — campaign status check happens after user confirms
            this.setState({
                showSupplierBudgetWarning: true,
                overBudgetSuppliers: overBudget,
                pendingSendItems: selectedItems
            });
        } else {
            // Budget is fine — check campaign status before sending placements
            this.checkCampaignStatusAndSend(selectedItems);
        }
    };

    /**
     * Fetches the latest campaign status and decides whether to prompt the user
     * to send the campaign to Media Ocean before sending placements.
     *
     * - If campaign API fails: proceeds directly to sendToMediaOcean.
     * - If mediaocean_status is "Campaign Sent" or "Synced": proceeds directly.
     * - Otherwise: shows a confirmation modal asking the user.
     *
     * @param {Array} selectedItems - Placements to be sent to Media Ocean
     */
    checkCampaignStatusAndSend = (selectedItems) => {
        this.props.setSpinnerCount(1);
        const { id: campaignId, client_id: clientId } = this.state.campaignObj;
        MediaOpsService.getCampaignDataById(campaignId, clientId, this.props?.versionOneApi)
            .then((campaignData) => {
                if (campaignData[0]?.mediaocean_status?.toLowerCase() === 'campaign sent' || campaignData[0]?.mediaocean_status?.toLowerCase() === 'synced') {
                    this.sendToMediaOcean(selectedItems);
                } else {
                    this.props.setSpinnerCount(0);
                    this.setState({
                        showCampaignStatusWarning: true,
                        pendingSendItems: selectedItems
                    });
                }
            })
            .catch(() => {
                // If campaign status check fails, proceed directly with placement send
                this.sendToMediaOcean(selectedItems);
            })
    };

    /**
     * Closes the supplier budget warning modal
     */
    closeSupplierBudgetWarning = () => {
        this.setState({
            showSupplierBudgetWarning: false,
            overBudgetSuppliers: [],
            pendingSendItems: []
        });
    };

    /**
     * Proceeds despite budget warnings — then checks campaign status before sending placements
     */
    proceedWithBudgetOverride = () => {
        const { pendingSendItems } = this.state;
        this.closeSupplierBudgetWarning();
        if (pendingSendItems.length) {
            this.checkCampaignStatusAndSend(pendingSendItems);
        }
    };

    /**
     * Closes the campaign status warning modal without taking further action
     */
    closeCampaignStatusWarning = () => {
        this.setState({ showCampaignStatusWarning: false });
    };

    /**
     * Sends the campaign to Media Ocean first, then sends the placements.
     * If the campaign send fails, proceeds with placement send anyway.
     */
    proceedSendCampaignThenPlacements = () => {
        const { pendingSendItems } = this.state;
        this.closeCampaignStatusWarning();

        const campaignId = encodeURIComponent(this.state.campaignObj.id);
        const channels = (this.state.campaignObj?.channels || []).map(channel => channel.channel_name);
        const payload = {
            campaignTaxonomy: this.state.campaignObj?.campaign_name,
            userEmail: this.props?.omniProfile?.email,
            channels,
            isCurrencyLocal: this.props?.systemParametersData?.data?.isLocal,
            campaignStartDate: this.state.campaignObj?.start_date,
            campaignEndDate: this.state.campaignObj?.end_date,
        };

        this.props.setSpinnerCount(1);
        MediaOpsService.sendCampaignToMediaOcean(payload, campaignId, this.props?.versionOneApi)
            .then(() => {
                this.props.setSpinnerCount(0);
                this.sendToMediaOcean(pendingSendItems);
            })
            .catch(() => {
                this.props.setSpinnerCount(0);
                this.sendToMediaOcean(pendingSendItems);
            });
    };

    /**
     * Skips the campaign send and proceeds directly with placement send
     */
    proceedSendPlacementsOnly = () => {
        const { pendingSendItems } = this.state;
        this.closeCampaignStatusWarning();
        if (pendingSendItems.length) {
            this.sendToMediaOcean(pendingSendItems);
        }
    };

    handleApprovalResponse = (response, value) => {
        const resultList = response.data.updateResults;
        // Analytics: approval results
        const successCount = resultList.filter(r => r.status === 'success').length;
        const failureCount = resultList.length - successCount;
        trackEvent(`Approval toggle completed.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, successCount, failureCount, action: value ? 'unapprove' : 'approve' });
        const alerts = resultList.map(result => {
            const status = result.status === "success" ? "success" : "danger";
            const title = `${MediaOpsService.getSource(result.status)} - Package/Placement is successfully ${value ? 'unapproved' : 'approved'}`;
            const bodyMsgArr = [
                html`<b>Placement/Package Name</b>:<br/>${result.placementTaxonomy}`,
                ...(result.status !== "success" ? [html`<b>Error description</b>:<br/>${result.validation}`] : []),
            ];
            return { type: status, title, bodyMsgArr };
        });

        AlertMessagesService.showMultipleAlerts(alerts);
    };

    handleApprovalError = (type, value, error = null) => {
        // Analytics: approval failed
        trackEvent(`Approval toggle failed.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id });
        
        let errorMessage = `Unable to ${value ? 'unapprove' : 'approve'} the selected placements`;
        let bodyMsgArr = [];
        
        if (error && error.chunkErrors) {
            const { successfulChunks, chunkErrors, totalProcessed, failedPlacements, successfulPlacements } = error;
            errorMessage = `Failed to ${value ? 'unapprove' : 'approve'} ${failedPlacements || 'some'} out of ${totalProcessed || 'selected'} placements`;
            bodyMsgArr = [
                `Successfully processed: ${successfulPlacements || successfulChunks} placements`,
                `Failed to process: ${failedPlacements || 'Unknown'} placements`,
                `Total selected: ${totalProcessed || 'Unknown'} placements`
            ];
            
            if (chunkErrors && chunkErrors.length > 0) {
                bodyMsgArr.push('');
                bodyMsgArr.push('Details by processing batch:');
                chunkErrors.forEach((chunkError) => {
                    bodyMsgArr.push(`Batch ${chunkError.chunkIndex}: ${chunkError.chunkSize} placements failed to process`);
                });
            }
        }
        
        AlertMessagesService.showMultipleAlerts([{
            type: 'danger',
            retryBtnText: 'Retry',
            retryBtnEvent: () => this.handleBuyingSystemChange(type, value),
            title: errorMessage,
            bodyMsgArr: bodyMsgArr,
        }]);
    };

    sendToMediaOcean = (selectedItems) => {
        this.props.setSpinnerCount(1);

        const payload = {
            ids: [],
            updatedBy: this.props?.omniProfile?.email,
            exportStatus: "Ready for Prisma",
            is_currency_local: this.props?.systemParametersData?.data?.isLocal,
        };

        const selectedPlacementNames = selectedItems
            .filter(item => !['DirectLine', 'ProgrammaticPlacement'].includes(item.package_type))
            .map(item => {
                const taxonomy = item['placement_taxonomy'];
                const placementId = item['placements_id'];
                payload.ids.push(item.id);
                return taxonomy.includes(':|') ? taxonomy : `${taxonomy}:|${placementId}`;
            });

        const campaignId = encodeURIComponent(this.state.campaignObj.id);

        PlacementDetailsTableService.sendToMediaOcean(payload, campaignId, this.props?.versionOneApi)
            .then((response) => {
                let jobId = response?.data?.async_send_result?.job_id;
                let message = response?.data?.async_send_result?.message;
                trackEvent(`Send to Buying System is in progress`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id });
                AlertMessagesService.showMultipleAlerts([{                
                    type: 'warning', 
                    title:'Send to Buying System is in progress',
                    bodyMsgArr: [message],
                }]);
                this.props.setSpinnerCount(0);
                this.checkJobStatusForSendToMo(jobId, selectedPlacementNames, selectedItems);
            })
            .catch((error) => {
                this.props.setSpinnerCount(0);
                trackEvent(`Send to Buying System failed (network/api).`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, error: error?.response?.data || error });
                AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.sendToMediaOcean(selectedItems), title: 'Unable to send the placements to Media Ocean', bodyMsgArr: []}]);
            })
    };

    /**
     * Function will call API repeatly to check job status
     * then it will call function on job "completion" that will open download modal
     * @param {Array} selectedItems 
     * @param {Array} pmdsItems 
     * @param {string} jobId 
     */
    checkJobStatusForSendToMo = (jobId, selectedPlacementNames, selectedItems) => {
        let payload = {jobId:jobId};
        PlacementDetailsTableService.checkExportJobStatus(payload)
        .then((response) => {
            let result = JSON.parse(response.result);
            if(response.status === "COMPLETED" || response.status === "COMPLETED WITH ERRORS"){
                this.handleSendToMediaOceanResponse(result, selectedPlacementNames, selectedItems);

            }else if(response.status === "IN PROGRESS" || response.status === "TRIGGERED"){
                setTimeout(() => {
                    this.checkJobStatusForSendToMo(jobId, selectedPlacementNames, selectedItems);
                }, 5000);
            }else {
                this.handleSendToMediaOceanError(result, selectedItems);
            }
            
        }, (error) => {
            trackEvent(`Send to Buying System failed (network/api).`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, error: error?.response?.data || error });
            this.handleSendToMediaOceanError(error, selectedItems)
        });
    }

    handleSendToMediaOceanResponse = (response, selectedPlacementNames, selectedItems) => {
        const result = response;
        const alerts = [];
        if (result.status === "success") {
            // Analytics: send to MO success
            trackEvent(`Send to Buying System succeeded.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, count: Object.keys(result.results || {}).length });
            Object.keys(result.results).forEach(placementName => {
                alerts.push({
                    type: "success",
                    title: `${MediaOpsService.getSource(result.status)} - Placement/Package is sent to Media Ocean`,
                    bodyMsgArr: [html`<b>Placement/Package Name</b>:<br/>${placementName}`],
                });
            });
        } else {
            // Analytics: send to MO failed
            trackEvent(`Send to Buying System failed.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id });
            this.moErrorObj.validationArr = [];
            if (!Object.keys(result?.validationResults || {}).length) {
                selectedPlacementNames.forEach((placementName, index) => {
                    const errorMessage = PlacementDetailsTableService.getErrorMessage(result, placementName);
                    const placementData = selectedItems[index];
                    // Extract field name from error message
                    const fieldName = this.extractFieldNameFromError(errorMessage);
                    
                    // Create error message - only show clickable link if we have a field name that can be edited
                    const errorDescriptionContent = fieldName 
                        ? html`<b>Error description</b>:<br/>${errorMessage} <a @click="${this.closeAlertAndEditPlacement.bind(this, placementData, index)}">Click here</a> to complete the action`
                        : html`<b>Error description</b>:<br/>${errorMessage}`;
                    
                    let alertData = {
                        type: "danger",
                        retryBtnText: 'Retry',
                        retryBtnEvent: () => this.sendToMediaOcean(selectedItems),
                        title: `${MediaOpsService.getSource(result.status)} - Unable to send to Media Ocean`,
                        bodyMsgArr: [
                            html`<b>Placement/Package Name</b>:<br/>${placementName}`,
                            errorDescriptionContent,
                        ],
                        msgData: { placementName, errorMessage, placementData }
                    }
                    alerts.push(alertData);
                    this.moErrorObj.validationArr.push({msg: errorMessage, alertData, fieldName});
                });
            } else {
                Object.keys(result.validationResults).forEach((placementName, index) => {
                    const errorMessage = result.validationResults[placementName];
                    // Find the corresponding placement object
                    const placementData = selectedItems.find(item => 
                        item.placement_taxonomy === placementName || 
                        (item.placement_taxonomy.includes(':|') ? 
                            item.placement_taxonomy.split(':|')[0] === placementName : false)
                    );
                    // Extract field name from error message
                    const fieldName = this.extractFieldNameFromError(errorMessage);
                    
                    // Create error message - only show clickable link if we have a field name that can be edited
                    const errorDescriptionContent = fieldName 
                        ? html`<b>Error description</b>:<br/>${errorMessage} <a @click="${this.closeAlertAndEditPlacement.bind(this, placementData, this.moErrorObj.validationArr.length)}">Click here</a> to complete the action`
                        : html`<b>Error description</b>:<br/>${errorMessage}`;
                    
                    let alertData = {
                        type: "danger",
                        retryBtnText: 'Retry',
                        retryBtnEvent: () => this.sendToMediaOcean(selectedItems),
                        title: `${MediaOpsService.getSource(result.status)} - Unable to send to Media Ocean`,
                        bodyMsgArr: [
                            html`<b>Placement/Package Name</b>:<br/>${placementName}`,
                            errorDescriptionContent,
                        ],
                        msgData: { placementName, errorMessage, placementData }
                    }
                    alerts.push(alertData);
                    this.moErrorObj.validationArr.push({msg: errorMessage, alertData, fieldName});
                });
            }
        }
        this.loadPlacements();
        setTimeout(() => {
            AlertMessagesService.showMultipleAlerts(alerts);
        }, 500);
    };

    /**
     * Handles errors that occur when sending placements to Media Ocean
     * @param {Error} error - The error object containing response data
     * @param {Array} selectedItems - The array of placement items that were selected to send
     * @returns {void} Shows an alert message with error details and retry option
     */
    handleSendToMediaOceanError = (error, selectedItems) => {
        const errorMessage = error?.response?.data?.error?.message || error?.message;
        // Analytics: send to MO failed (network/api)
        trackEvent(`Send to Buying System failed (network/api).`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, error: error?.response?.data || error });
        AlertMessagesService.showMultipleAlerts([{
            type: 'danger',
            retryBtnText: 'Retry',
            retryBtnEvent: () => this.sendToMediaOcean(selectedItems),
            title: 'Unable to send the placements to Media Ocean',
            bodyMsgArr: errorMessage ? [html`<b>Error description</b>:<br/>${errorMessage}`] : [],
        }]);
    };

    /**
     * Extracts field name from error message to help identify the problematic field
     * @param {string} errorMessage - The error message from Media Ocean API
     * @returns {string|null} - The extracted field name or null if not found
     */
    extractFieldNameFromError = (errorMessage) => {
        // Common error patterns in Media Ocean responses
        const fieldPatterns = [
            /missing required field[:\s]+([^.,:]+)/i,
            /field ([^.,:]+) (is required|cannot be empty)/i,
            /invalid value for ([^.,:]+)/i,
            /([^.,:]+) is not valid/i,
            // Added patterns to catch "field: field not valid" format
            /([^.,:]+):\s*([^.,:]+)\s*not valid/i,
            /([^.,:]+):\s*([^.,:]+)\s*invalid/i,
            // Generic pattern to catch field names followed by error descriptions
            /^([^.,:]+):\s*(?!$)/i
        ];

        for (const pattern of fieldPatterns) {
            const match = errorMessage.match(pattern);
            if (match && match[1]) {
                // For patterns like "Buy category: Buy category not valid"
                // Return the first captured group which is the field name
                const fieldName = match[1].trim();
                return fieldName;
            }
        }
        
        // If no match found but message contains a colon, use the text before the colon as field name
        if (errorMessage.includes(':')) {
            return errorMessage.split(':')[0].trim();
        }
        
        return null;
    }

    /**
     * The below function is called when the user clicks on 'Click here' action in the MO alert message.
     * It closes the alert message and opens the edit package/placement modal.
     * @param {Object} placement - The placement object.
     * @param {number} errorIndex - The error index.
     */
    closeAlertAndEditPlacement = (placement, errorIndex) => {
        document.getElementsByTagName('custom-alert')[0].closeModal(0);
        this.moErrorObj.index = errorIndex;
        // Add the error field information to the placement object
        const errorData = this.moErrorObj.validationArr[errorIndex];
        if (errorData && errorData.fieldName) {
            placement.moErrorField = errorData.fieldName;
            placement.moErrorMessage = errorData.msg;
        }
        this.editPlacement({data: placement}, true);
    }

    /**
     * Handles the creation of programmatic types and updates the component's state accordingly.
     * If the selected menu item does have a `taxonomyType` property but no value, the function returns without performing any actions.
     * @param {object} menuItem - The selected menu item with information about the programmatic type.
     * @returns {void}
     */
    createProgrammaticTypes = (menuItem) => {
        if (menuItem.hasOwnProperty('taxonomyType') && !menuItem.taxonomyType) return;

        const createProgrammaticCreative = this.state.selectedItems.find(v => v.package_type === "ProgrammaticPackage");
        // If the selected menu item is 'Creative Placement' and the selected items include a 'ProgrammaticPackage', open programmatic create package edit modal else open creative modal.
        if (["Placement", "Line Item", "Creative Placement",].includes(menuItem.title) && menuItem.type.includes("Programmatic") && createProgrammaticCreative) {
            this.editPlacement({data:createProgrammaticCreative});
            return;
        }
        this.setState({ placementCategory: menuItem, isPlacementModalOpen: true });
        if (this.state.selectedItems.length) {
            if (["ProgrammaticPackage", "DSP"].includes(this.state.selectedItems[0].package_type)) {
                this.setState({ programmaticParentId: this.state.selectedItems[0].id })
            }
        }
    }

    /** The below code is defining a function called `getDialogSelectedItems` in JavaScript. This
     * function returns a filtered list of selected items based on the value of `placementCategory` in
     * the state object. 
     */
    getDialogSelectedItems = () => {
        let selectedItems = this.state.selectedItems;
        let isDirectLineTaxonomyAvailable = this.allTaxonomyData.directLineTaxonomyData?.fields?.length;
        let isProgrammaticPlacementAvailable = this.allTaxonomyData.programmaticPlacementTaxonomyData?.fields?.length;
        if (this.state.editDailog && this.state.placementCategory?.title === 'Package') {
            return [];
        }
        if (this.state.placementCategory?.type?.includes('Direct')) {
            this.filterSelectedDirectPlacement(isDirectLineTaxonomyAvailable, selectedItems)
        }
        if (this.state.placementCategory?.type?.includes('Programmatic')) {
            this.filterSelectedprogrammaticPlacement(isProgrammaticPlacementAvailable, selectedItems)
        }
        if (this.state.placementCategory?.title?.includes('Estimate Name') && this.DirectProgrammaticMainTypes.includes(this.state.placementCategory?.type)) {
            return this.state.selectedItems.filter(v => v.placement_type === this.state.placementCategory?.type);
        }
        return selectedItems;
    }

    /**
     * Filter if direct placement selected
     * @param {boolean} isDirectLineTaxonomyAvailable 
     * @param {array} selectedItems 
     * @returns filter placement
     */
    filterSelectedDirectPlacement = (isDirectLineTaxonomyAvailable, selectedItems) => {
        switch (this.state.placementCategory.title) {
            case "Placement":
                return selectedItems.filter(v => v.package_type === "Package");
            case "Line Item":
                let standAloneCount = selectedItems.filter(v => v.package_type === "Standalone");
                if (standAloneCount.length > 0) {
                    return selectedItems;
                }
                return selectedItems.filter(v => v.package_type === "Child");
            case "Creative Placement":
                if (isDirectLineTaxonomyAvailable) {
                    return selectedItems.filter(v => v.package_type === "DirectLine");
                } else {
                    let standAloneCount = selectedItems.filter(v => v.package_type === "Standalone");
                    if (standAloneCount.length > 0) {
                        return selectedItems;
                    }
                    return selectedItems.filter(v => v.package_type === "Child");
                }
            default:
                break;
        }
    }

    /**
     * Filter placement if programmatic is selected
     * @param {boolean} isProgrammaticPlacementAvailable 
     * @param {Array} selectedItems 
     * @returns filter placement
     */
    filterSelectedprogrammaticPlacement = (isProgrammaticPlacementAvailable, selectedItems) => {
        switch (this.state.placementCategory.title) {
            case "Placement":
                return selectedItems.filter(v => v.package_type === "ProgrammaticPackage");
            case "Line Item":
                if (isProgrammaticPlacementAvailable) {
                    return selectedItems.filter(v => v.package_type === "ProgrammaticPlacement");
                } else {
                    return selectedItems.filter(v => v.package_type === "ProgrammaticPackage");
                }
            case "Creative Placement":
                return selectedItems.filter(v => v.package_type === "DSP");
            default:
                break;
        }
    }
    /**
     * Update the selected items and disabled menu options based on the provided row object.
     * @param {Array} nestedData - The array of nested data objects. 
     *  It should have an 'id' property to uniquely identify the item.
     *  It may have a 'checked' property indicating whether the item is partial checked or not.
     *  If nestedData is null or undefined, all selected items will be cleared.
     */
    updateSelectedItemsInState() {
        let enableDirectLine, enableDirectCreative, enableProgrammaticPlacement, enableProgrammaticLine, enableProgrammaticCreative = false;
        let packageCount = this.state.selectedItems.filter(item => item.package_type?.includes('ProgrammaticPackage'));
        let directPlacementCount = this.state.selectedItems.filter(item => item.package_type === 'Child' && item.parent_type !== 'DSP');
        let directLineCount = this.state.selectedItems.filter(item => item.package_type === 'DirectLine');
        let programmaticPlacementCount = this.state.selectedItems.filter(item => item.package_type === 'ProgrammaticPlacement');
        let programmaticLineCount = this.state.selectedItems.filter(item => item.package_type === 'DSP');
        let standAloneCount = this.state.selectedItems.filter(item => item.package_type === 'Standalone');

        if (packageCount.length === 1 && packageCount[0].package_type === 'ProgrammaticPackage') {
            if (this.allTaxonomyData.programmaticPlacementTaxonomyData?.fields?.length) {
                enableProgrammaticPlacement = true;
            }
            else {
                enableProgrammaticLine = true;
            }
        }

        if ((directPlacementCount.length === 1 && standAloneCount.length === 0) || (standAloneCount.length === 1 && directPlacementCount.length === 0)) {
            enableDirectLine = !!this.allTaxonomyData?.directLineTaxonomyData?.fields?.length;
            enableDirectCreative = true;
        }
        if (directLineCount.length === 1 && directPlacementCount.length === 0 && standAloneCount.length === 0) {
            enableDirectCreative = true;
        }
        if (programmaticPlacementCount.length === 1) {
            enableProgrammaticLine = true;
        }
        if (programmaticLineCount.length === 1) {
            enableProgrammaticCreative = true;
        }
        this.setDisabledMenuItems(this.channelDropdownList, { enableDirectLine, enableDirectCreative, enableProgrammaticPlacement, enableProgrammaticLine, enableProgrammaticCreative });
    }

    /* The below code is defining a function called `CallLineItemProgramatically` that takes in a
    * parameter called `ProgrammaticPackageTempPayload`. Inside the function, it sets the state of
    * `ProgrammaticPackageTempData` to the value of `ProgrammaticPackageTempPayload`, closes the
    * current component, and calls another function called `createProgrammaticTypes` with an object
    * containing a `title` and `type` property. 
    */
    CallLineItemProgramatically = (ProgrammaticPackageTempPayload, parentTitle, parentType) => {
        this.setState({ ProgrammaticPackageTempData: ProgrammaticPackageTempPayload });
        this.close();
        if (parentTitle && parentTitle === "Package") {
            this.createProgrammaticTypes({ title: "Placement", type: parentType.includes('Social') ? "Social Direct" : "Direct" });
        } else if (parentTitle && parentTitle !== "Package" && this.DirectProgrammaticTypes.includes(parentTitle)) {
            this.createProgrammaticTypes({ title: placementCreateMenuTypes[parentType]['Offline Estimate Name']?.title, type: parentType });
        }
    }

    /**
     * onPlacementCreate - Handles the creation of a new placement.
     * @param {boolean} is_add_next - Indicates whether an additional action should be performed after creating the placement.
     * @param {string} placementType - The type of placement to be created.
     * @param {boolean} isMoEditFix - Indicates whether the placement is being updated for a MO error.
     * @returns {void} - This function does not return a value directly. It may execute additional actions based on the provided parameters.
     */
    onPlacementCreate = (is_add_next, placementType, isMoEditFix = false) => {
        // First handle the MO error case before any state changes or reloading
        if(isMoEditFix && this.moErrorObj.validationArr){
            // Store current validation array to work with
            const currentValidationArr = [...this.moErrorObj.validationArr];
            
            // Remove the fixed error from our copy
            currentValidationArr.splice(this.moErrorObj.index, 1);
            
            // Update the actual validation array
            this.moErrorObj.validationArr = currentValidationArr;
            
            // If there are remaining errors, show them with updated indices
            if(currentValidationArr.length){
                let alertData = [];
                for(let i = 0; i < currentValidationArr.length; i++){
                    const data = currentValidationArr[i].alertData.msgData;
                    // Make sure we're using the correct function and updating indices
                    currentValidationArr[i].alertData.bodyMsgArr = [
                        html`<b>Placement/Package Name</b>:<br/>${data.placementName}`,
                        html`<b>Error description</b>:<br/>${data.errorMessage} <a @click="${this.closeAlertAndEditPlacement.bind(this, data.placementData, i)}">Click here</a> to complete the action`,
                    ];
                    alertData.push(currentValidationArr[i].alertData);
                }
                
                // Show alerts with a slight delay to ensure they appear after state changes
                setTimeout(() => {
                    AlertMessagesService.showMultipleAlerts(alertData);
                }, 100);
            }
        }
        
        // Clear smart suggestions cache after placement creation
        this.smartSuggestionsCache = {}; // Clear component-level cache
        
        // Now handle the regular placement creation flow
        this.setState({selectedItems:[]});
        this.setDisabledMenuItems(this.channelDropdownList, null);
        if(is_add_next){
           this.saveAndAddNext(placementType);
        }
    }

    /**
     * Function to save and add the next programmatic placement based on the provided placement type.
     * @param {string} placementType - The type of programmatic placement to be created (e.g., "Placement", "Search Placement", "Programmatic Package", "Line Item", "Creative Placement").
     * @returns {void} - This function does not return anything.
     */
    saveAndAddNext = (placementType) => {
        this.close();
        if(placementType === "Placement"){
            this.createProgrammaticTypes({title: "Placement",type: "Direct"});
        }else if(placementType === "Social Placement"){
            this.createProgrammaticTypes({title: "Placement",type: "Social Direct"});
        }else if(placementType === "Search Placement"){
            this.createProgrammaticTypes({title: "Paid Search",type: "Search"});
        }else if(placementType === "Programmatic Package"){
            this.createProgrammaticTypes({title: 'Package', type: 'Programmatic'});
        }else if(placementType === "Line Item"){
            this.createProgrammaticTypes({title: 'Line Item', type: 'Programmatic'});
        }else if(placementType === "Creative Placement"){
            this.createProgrammaticTypes({title: 'Creative Placement', type: 'Programmatic'});
        }else if(placementType === "Fee"){
            this.createProgrammaticTypes({title: 'Fee type', type: 'Fee'});
        } else {
            this.createProgrammaticTypes({title: this.state.placementCategory.title, type: this.state.placementCategory.type});
        }
    }

    /**
     * set placement type and open Edit modal
     * @param {object} props 
     * @param {boolean} isMoErrorEdit - Flag indicating if this edit is triggered from a Media Ocean error
     */
    editPlacement = (props, isMoErrorEdit = false) => {
        // Clear smart suggestions cache before editing
        this.smartSuggestionsCache = {}; // Clear component-level cache
        
        let placementData = props?.data;
        //props had old data sometime, getting updated data from placementsFromApi state  
        if(placementData?.package_type && !placementData?.package_type.includes('Programmatic')){
            placementData = this.state.placementsFromApi.find((item) => item.id === Number(placementData.id) || item.id === placementData.id)
        }
        let directFlag = placementData?.package_type === "Package" ? true : false;
        let isSocial = placementData?.placement_type?.includes('Social') ? true : false;
        // Check for the packageType of the edit placement and set the state for the package/programmatic type
        let editType = PlacementUtils.mapPackageType(placementData?.package_type, placementData.placement_type, placementData.parent_type);
        // Open programmatic package modal for creation or edit of line item or programmatic creative
        if(placementData?.package_type === "ProgrammaticPackage"){
            placementData = this.getEditData(placementData,placementData.id);
        }
        else if (placementData?.package_type === 'ProgrammaticPlacement' && placementData?.parent_id) {
            editType = { title: "Package", type: isSocial ? "Social Programmatic" : "Programmatic" };
            placementData = this.getEditData(placementData);
        } else if (placementData?.package_type === 'DSP' && placementData?.parent_id) {
            editType = { title: "Package", type: isSocial ? "Social Programmatic" : "Programmatic" };
            let dspParentData = this.state.placementsFromApi.find((item) => item.id === Number(placementData.parent_id));
            // Check for the parent of the DSP parent, if there is a placement
            // If there is no placement, then use the DSP parent
            if (dspParentData.package_type === "ProgrammaticPackage") {
                placementData = this.getEditData(placementData);;
            }else{
                placementData = this.getEditData(dspParentData);;
            }
        } else if ((placementData?.package_type === "Child" && placementData?.parent_type === "DSP") && placementData?.parent_id) {
            editType = { title: "Package", type: isSocial ? "Social Programmatic" : "Programmatic" };
            let dspData = this.state.placementsFromApi.find((item) => item.id === Number(placementData.parent_id));
            let dspParentData = this.state.placementsFromApi.find((item) => item.id === Number(dspData.parent_id));
            // Check for the parent of the DSP parent, if there is a placement
            // If there is no placement, then use the DSP parent
            if (dspParentData.package_type === "ProgrammaticPackage") {
                placementData = this.getEditData(dspData);;
            }else{
                placementData = this.getEditData(dspParentData);;
            }
        }
        let placementCategory = {
            title: editType?.title,
            type: editType?.type,
            taxonomyType: directFlag ? "DirectPackage" : ''
        }
        // Check for the MO error edit and assign the MO error data to the placement data
        if(isMoErrorEdit){
            placementData.moErrorData = this.moErrorObj.validationArr[this.moErrorObj.index];
        }
        // Store isMoErrorEdit flag in state so close() can detect if this was a Media Ocean error fix
        this.setState({ 
            editDailog: true, 
            isPlacementModalOpen: true, 
            placementData, 
            placementCategory: placementCategory,
            isMoErrorEdit: isMoErrorEdit  // Add this line to track if this edit was triggered from MO error
        });
    }

    getEditData = (placement, id = null) => {
        const compareId = id ? id : placement.parent_id
        const placementData = Utils.findObjectByIdInNestedDataInAggridData(this.state.placementsWithNestedStructure, compareId);
        return placementData;
    }

    getPlacementsName = (placement,placementColumn) => {
        if (['Delivered', 'Ad Server', 'Ad Verification'].includes(placement?.package_type) || ['accepted'].includes(placement?.export_status?.toLowerCase()) ) {
            return ;
        }
        
        let syntheticId = '';
        let taxonomySchemaArray = this.allTaxonomyData?.defaultTaxonomyData;
        let override_taxonomy = placement.override_taxonomy ;
        let taxonomyNameString = placement.parameter_code;
        if(placementColumn !== "parameter_code"){
            taxonomySchemaArray = PlacementUtils.getDialogTaxonomyData(this.state, PlacementUtils.mapPackageType(placement.package_type, placement.placement_type, placement.parent_type),this.placementTaxonomyMap, this.allTaxonomyData);
            let taxonomyNamrrya = placement.placement_taxonomy.split(':|');
            taxonomyNameString = taxonomyNamrrya[0];
            syntheticId = taxonomyNamrrya[1];
        }

        let editFormData = {}
        let schemaString = '';
        if((!placement.override_taxonomy || placementColumn === "parameter_code")){
            // placementObj = PlacementDetailsTableService.getFormEditData(taxonomySchemaArray?.fields,placement,placementColumn);
            let placementJson = placementColumn === "parameter_code" ? null : (placement?.taxonomy ? placement.taxonomy: null);
            schemaString = placementColumn === "parameter_code" ? placement.parameter_code_taxonomy_schema : placement.taxonomy_schema
            //if placement is too old and there is taxonomy_ids_schema
            if(!placementJson?.length && placement?.taxonomy_ids_schema && placementColumn !== "parameter_code"){
                let taxonomyIds = placement?.taxonomy_ids_schema.split('|').map(val=> {
                    return val.split(' *')[1]
                });
            
                let newSchema = [];
                taxonomyIds.forEach((id,index) => { 
                    let field = {};
                    field = taxonomySchemaArray?.fields.find(field=> id == field.id)
                    let name = field?.name || field?.alias;
                    newSchema.push(name);
                });

                schemaString = newSchema.join('|');
                placement.taxonomy_schema = schemaString;
            }
            editFormData = PlacementDetailsTableService.splitPlacementStringIntoSchemaObject(taxonomyNameString, schemaString, taxonomySchemaArray?.fields, ':|', true,placementJson);
        }

        // let editFormData = Object.fromEntries(Object.entries(formObj).filter(([_, v]) => v !== undefined));
        let isNameAndSchemaFieldMatch = PlacementDetailsTableService.compareTaxonomyAndData(taxonomySchemaArray?.fields, editFormData, schemaString, placement?.taxonomy);
        if(placement.data_source === 'planit' && placement.id.includes('planit') || !taxonomySchemaArray){
            return;
        }
        if((override_taxonomy && placementColumn !== "parameter_code") || !isNameAndSchemaFieldMatch){
            this.isInlineOverride = true;
        } 
        this.placementRowData = placement;
        const taxonomyKeys = {
                'schema' : placementColumn === "parameter_code"? 'parameter_code_taxonomy_schema' :'taxonomy_schema', 
                'displayName' : placementColumn === "parameter_code" ? 'parameter_code_taxonomy_display' : 'placement_taxonomy_display', 
                'taxonomyName' : placementColumn, 
                'syntheticId': syntheticId,
                'overrideName': override_taxonomy ? placement.overriden_placement_taxonomy : placement.placement_taxonomy
            }      
        this.setState({displayInlineEditModal: true,editFormData:editFormData,taxonomySchemaArray:taxonomySchemaArray,taxonomyKeys});
    }
    

    /**
     * Closes the placement modal.
     * @returns {void}
     */
    close = (isSuccess=null) => {
        this.placementRowData = {};
        this.isInlineOverride = false;
        // Clear the isMoErrorEdit flag when closing the modal
        this.setState({ 
            isPlacementModalOpen: false, 
            editDailog: false,
            displayInlineEditModal: false,
            isMoErrorEdit: false
        });
        if(isSuccess && this.state.isMoErrorEdit){
            // If this was a success and it was a MO error edit, call onPlacementCreate with isMoEditFix=true
            this.onPlacementCreate(false, null, true);
        } else if(isSuccess){
            // placement was created/updated - reload placements and then refresh campaign level data
            this.loadPlacements(false, true);
        }
    }

    /**
     * Opens the creative screen with the current campaign object, taxonomy data and vendor mappings.
     * @returns {void}
    */
    openCreative = () => {
        let currentCampaign = this.state.campaignObj;
        currentCampaign.vendorMappingsData = this.vendorMappingMap;
        currentCampaign.defaultTaxonomyData = this.allTaxonomyData.defaultTaxonomyData;
        currentCampaign.placementData = this.state.placementsFromApi;
        Utils.navigateToComponent(currentCampaign, 'createCreatives', this.props);
    }

    setActiveTab = (tab) => {
        if (this.state.archiveTab !== tab) {
            trackEvent('Placements tab changed', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, tabName: tab ? 'Archived' : 'Active' });
            this.setState({ archiveTab: tab, placementsWithNestedStructure: [], placementsFromApi: [] }, () => {
                this.loadPlacements();
            });
        }
    };
// Export and Import functions
    /**
    * Handles the click event for the import/export buttons, which either exports the media plan
    * or opens the file browser to import a media plan.
    * @param {Object} event The event object.
    * @returns {void}
    */
    importExportClick = (event) => {
        const label = event?.detail?.value?.label;
        trackEvent('Import/Export option selected', { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, optionLabel: label });
        if (label === 'Export media plan') {
            this.exportPlacement()
        } else if (label === 'Import media plan') {
            this.campaignIdToImportPlacements = this.state.campaignObj.id;
            document.getElementById('fileInput').click();
        } else if (label === 'Edit template') {
            this.openEditTemplateModal();
        }
    }
// export Placement method
    exportPlacement = () => {
        let selectedItems = [];
        let pmdsItems = [];
        // Check for all the selected items, either all or partial(in case of child placements) and push them to the selectedItems array
        let flatData = this.state.selectedItems;
        for(let i=0;i<flatData.length;i++){
            if(flatData[i].id && flatData[i].data_source !== 'planit' ){
                selectedItems.push(flatData[i]);
            }
            // Check for PMDS package and push them to the pmdsItems array
            if(flatData[i].data_source === 'planit' ){
                pmdsItems.push(flatData[i]);
            }
        }
        trackEvent(`Export placements initiated.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, selectedCount: selectedItems.length, pmdsCount: pmdsItems.length });
        this.callExportAPI(selectedItems,pmdsItems);
    }


    callExportAPI = (selectedItems,pmdsItems) => {
        this.props.setSpinnerCount(1);
        this.setState({ isExportInProgress: true }); // Start export
        let campaignId = encodeURIComponent(this.state.campaignObj.id);
        PlacementDetailsTableService.exportPlacements(selectedItems, campaignId, this.setCampaignPayload(), this.props?.versionOneApi, pmdsItems)
        .then((response) => {
            trackEvent(`Export job started.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id });
            AlertMessagesService.showMultipleAlerts([{                
                retryBtnText: 'Retry', 
                retryBtnEvent: () => this.callExportAPI(selectedItems,pmdsItems), 
                type: 'warning', 
                title:'Preparing your file.',
                bodyMsgArr: [
                    'Do not press back or refresh the browser or you may',
                    'not get your file to download.'
                ],
            }]);
            this.checkJobStatusForExportPlacement(selectedItems,pmdsItems,response.job_id);
            this.props.setSpinnerCount(0); // Spinner might be better handled after job completion or failure
        }, (error) => {
            this.setState({ isExportInProgress: false }); // End export on error
            trackEvent(`Export placements failed to start.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, error: error?.response?.data || error });
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.callExportAPI(selectedItems,pmdsItems), title: 'Unable to export the placements', bodyMsgArr: []}]);
            this.props.setSpinnerCount(0);
        });
    }
    
     /**
     * Function will call API repeatly to check job status
     * then it will call function on job "completion" that will open download modal
     * @param {Array} selectedItems 
     * @param {Array} pmdsItems 
     * @param {string} jobId 
     */
    checkJobStatusForExportPlacement = (selectedItems,pmdsItems,jobId) => {
        let payload = {jobId:jobId};
        PlacementDetailsTableService.checkExportJobStatus(payload)
        .then((response) => {
            if(response.status === "COMPLETED" && response.s3PresignedUrl){
                this.setState({ isExportInProgress: false }); // End export on success
                Utils.showDownloadModal(response);
                trackEvent(`Export job completed.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id });
                
            }else if(response.status === "IN PROGRESS" || response.status === "TRIGGERED"){
                setTimeout(() => {
                    this.checkJobStatusForExportPlacement(selectedItems,pmdsItems,jobId);
                },10000);
            }else {
                this.setState({ isExportInProgress: false }); // End export on other status
                AlertMessagesService.showErrorToastMessage("Set up your server to enable file download. Contact your team administrator for assistance.");
                trackEvent(`Export job not completed.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, status: response?.status });
            }
        }, (error) => {
            this.setState({ isExportInProgress: false }); // End export on error
            trackEvent(`Export job status check failed.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, error: error?.response?.data || error });
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.callExportAPI(selectedItems,pmdsItems), title: 'Unable to export the placements', bodyMsgArr: []}]);
        });
    }
    
    /**
    * The below function will be called during export placements to set the campaign payload in the POST API
    * @function setCampaignPayload
    * @return {Object} campaign payload object
    */
    setCampaignPayload = () => {
        if(this.state.campaignObj && this.props.campaignTaxonomyData){
            let delimiterValue = this.props.campaignTaxonomyData.fields[0]['delimiter_value'];
            let fieldvalue = [];
            let campaignTaxonomy = '';
            if (this.state.campaignObj?.campaignTaxonomy?.split(':|')[0] !== "-"  && this.state.campaignObj?.campaignTaxonomy?.split(':|')[0]) {
                campaignTaxonomy = this.state.campaignObj.campaignTaxonomy.split(':|')[0];
            } else {
                campaignTaxonomy = this.genrateCampaignTaxonomyName();
            }
            campaignTaxonomy.split(delimiterValue).map((item, nameIndex) => {
                let taxonomyField = this.props.campaignTaxonomyData.fields[nameIndex];
                let name = taxonomyField?.alias || taxonomyField?.name
                fieldvalue.push({
                    fieldName: name,
                    fieldValue: item
                })
            });
            let channelData = PlacementUtils.setChannelData();
            let partners = (this.state.campaignObj?.partners || []);
            return {
                campaigntaxonomy: campaignTaxonomy,
                campaignid: this.state.campaignObj.id,
                camapingtaxonomyfieldvalue: fieldvalue,
                campaignstartdate: this.state.campaignObj.start_date,
                campaignenddate: this.state.campaignObj.end_date,
                channels: channelData,
                partners: partners,
                is_currency_local: this.props?.systemParametersData?.data?.isCurrencyLocal
            }
        }
        return {}
    }
    /**
     * The below function will called to create campaaign taxonomy name when campaign taxonomy is empty in campaign object
     * @function setCampaignPayload
     * @return {String} camapign taxonomy
    */
    genrateCampaignTaxonomyName() {
        let campaignTaxonomyName = "";
        this.props.campaignTaxonomyData && this.props.campaignTaxonomyData.fields.forEach((field) => {
          if (field.name.toLowerCase() === 'channel') {
            if (this.state.campaignObj['channels'].length > 1) {
              campaignTaxonomyName += 'MUL' + field['delimiter_value'];
            } else if (this.state.campaignObj['channels'].length === 1) {
              campaignTaxonomyName += this.state.campaignObj['channels'][0]['channel_name'] || "NU";
              campaignTaxonomyName += field['delimiter_value'];
            } else {
              campaignTaxonomyName += "NU" + field['delimiter_value'];
            }
          } else {
                switch(field.field_type){
                    case 1:
                    let fieldCodeValue, fieldName = "";
                    fieldCodeValue = field.fielditems.filter(fielditem => fielditem['name'] === this.state.campaignObj[field.name.toLowerCase()]).map((fielditem) => {
                        fieldName = fielditem.name || "";
                        return fielditem.code;
                    })
                    campaignTaxonomyName += (fieldCodeValue[0] || "NU") + field['delimiter_value']
                    break;
                    case 2:
                    campaignTaxonomyName += (this.state.campaignObj[field.name.toLowerCase()] || "NU") + field['delimiter_value']
                    break;
                    case 9:
                    campaignTaxonomyName += field.static_field_value + field['delimiter_value']
                    break;
                    default:
                    campaignTaxonomyName += (this.state.campaignObj[field.name.toLowerCase()] || "NU") + field['delimiter_value']
                    break;
                }
            }
        });
        return campaignTaxonomyName.slice(0, -1);
    }

    //Import Placement
    /**
     * Attaches a file upload event handler to a file input element.
     * When a file is selected, it triggers the importMediaPlan function.
     */
    addFileUploadEventHandler = () => {
        const fileInput = document.getElementById('fileInput');
        // click event listener to clear the file input value during each import operation.
        fileInput.addEventListener('click', (event)=>{
            event.stopImmediatePropagation();
            fileInput.value = null;
        })
        // Add a change event listener to the file input element.
        fileInput.addEventListener('change', this.importMediaPlan);
    }

    /**
     * Imports a media plan from a selected file using FileReader and XLSX.js.
     */
    importMediaPlan = (event) => {
        event.stopImmediatePropagation();
        const fileInput = document.getElementById('fileInput');
        const file = fileInput.files[0];
        if (file) {
            trackEvent(`Import file selected.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, name: file.name, size: file.size, type: file.type });
        }
        const reader = new FileReader();
            // When the file is loaded, this event handler is triggered.
        reader.onload = async (event) => {
            // Read the loaded data as an ArrayBuffer.
            const data = new Uint8Array(event.target.result);
            const XLSX = await import('xlsx');
            // Parse the data into a workbook using XLSX.js.
            const workbook = XLSX.read(data, { type: 'array' });
            this.createPayloadFromWebBook(event, workbook, XLSX);
        };
        // Read the selected file as an ArrayBuffer.
        reader.readAsArrayBuffer(file);
    }
    
    /**
     * Get placementList from webbooc sheet. And call import API
     * @param {object} event 
     * @param {object} workbook 
     */
    createPayloadFromWebBook = (event, workbook, XLSX) => {
        // Initialize an empty object to store placement data.
        const placementTypes = ["Direct Placements", "Programmatic Placements", "Creative Placements", "Search Placements", "Social Directs", "Social Programmatics", "Offline Placements","Digital OOH"]; 
        let placementsPayload = {};
        // Iterate through each sheet in the workbook.
        workbook.SheetNames.forEach((sheetName) => {
            if (placementTypes.includes(sheetName)) {
                const sheet = workbook.Sheets[sheetName];
                // Convert the sheet data to JSON and filter rows with non-empty "Placement Taxonomy" values.
                const rows = XLSX.utils.sheet_to_json(sheet, {raw: false, header: 1, blankrows: true });
                //Check for the placement taxonomy row, save all the keys in an array and save the successsive rows in the placementsPayload object
                let taxonomyKeys = [];
                let isTaxonomyRow = false;
                for(let i=0;i<rows.length;i++){
                    const isPackgeAvialble = rows[i]?.some(item => 
                        item.replace(/\*/g, '').toLowerCase().trim() === "package type"
                    )
                    if(isPackgeAvialble){
                        taxonomyKeys = rows[i];
                        isTaxonomyRow = true;
                        placementsPayload[sheetName] = [];
                        continue;
                    }
                    if(isTaxonomyRow && (rows[i][0] && rows[i][0] !== "" || rows[i][2] && rows[i][2] !== "")){
                        let taxonomyValues = rows[i];
                        let taxonomyObject = taxonomyValues.reduce(function(reducer, fieldValue, index) {
                            reducer[taxonomyKeys[index]] = fieldValue ? fieldValue : 'NU';
                            return reducer;
                        }, {});
                        if (!taxonomyObject.ID && taxonomyObject?.TaxonomyValueID){
                            let taxonomyKeys = Object.keys(taxonomyObject).filter(key => key.includes("Placement Taxonomy"));
                            for(let i=0;i<taxonomyKeys.length;i++){
                                let placementTaxonomyColumn = taxonomyKeys[i];
                                if(taxonomyObject[placementTaxonomyColumn] && taxonomyObject[placementTaxonomyColumn].includes(taxonomyObject.TaxonomyValueID)){
                                    let taxonomyValueID = taxonomyObject.TaxonomyValueID;
                                    taxonomyObject[placementTaxonomyColumn] = taxonomyObject[placementTaxonomyColumn].replace(taxonomyValueID, "{TaxonomyValueID}");
                                    break;
                                }
                            }
                            taxonomyObject.TaxonomyValueID='{TaxonomyValueID}';
                        }
                        placementsPayload[sheetName].push(taxonomyObject);
                    }
                }
            }
        });
        this.props.setSpinnerCount(1);
        trackEvent(`Import file parsed.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id });
        this.SendImportRequest(placementsPayload,event);
    }

    /**
     * To call import API 
     * @param {object} placementsPayload 
     * @param {object} event 
     */
    SendImportRequest = (placementsPayload,event) =>{
        let campaignId = encodeURIComponent(this.campaignIdToImportPlacements);
        trackEvent(`Import request sent.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id });
        let payload = { "placements": placementsPayload, "userEmail": this.props?.omniProfile?.email, "is_currency_local": this.props?.systemParametersData?.data?.isCurrencyLocal, "partners": this.state.campaignObj?.partners || [] };
        PlacementDetailsTableService.importPlacements(campaignId, payload, this.props?.versionOneApi)
        .then((response) => {
            let respMsgArr = [];
            if(response?.data?.errors.length){
                // Handle any errors that occurred during the import process.
                this.handleImportError(response?.data,event);
                this.loadPlacements();
                trackEvent(`Import completed with errors.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, errorsCount: response?.data?.errors?.length });
                this.props.setSpinnerCount(0);
            }else{
                const successCode = response?.status;
                const placementsAdded = response?.data?.placementsAdded;
                const placementsUpdated = response?.data?.placementsUpdated;
                let isAllPlacementSuccess = response?.data?.AllPlacementsSuccess;
                let placements = response.data?.result?.Placements || [];
                // Construct the response message array based on the import results.
                let bodyMsg = this.OnImportSuccess(placements,placementsAdded,placementsUpdated,isAllPlacementSuccess);
    
                if((placementsAdded !== undefined && placementsAdded !== null) && (placementsUpdated !== undefined && placementsUpdated !== null) && !isAllPlacementSuccess){
                    respMsgArr.push({type:"warning",title:`${successCode} - Imported Media Plan with errors`,"bodyMsgArr":[bodyMsg]})
                    trackEvent(`Import completed with warnings.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, added: placementsAdded, updated: placementsUpdated, allSuccess: false });
                }else if((placementsAdded !== undefined && placementsAdded !== null) && (placementsUpdated !== undefined && placementsUpdated !== null) && isAllPlacementSuccess){
                    respMsgArr.push({type:"success",title:`${successCode} - Imported Media Plan successfully`,"bodyMsgArr":[html`${placementsAdded} placements added successfully.<br/>${placementsUpdated} placements updated successfully.`]})
                    trackEvent(`Import completed successfully.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, added: placementsAdded, updated: placementsUpdated, allSuccess: true });
                }else{
                    respMsgArr.push({type:"success",title:`${successCode} - Imported Media Plan successfully`,"bodyMsgArr":['Success message: Placements are Imported!']})
                    trackEvent(`Import completed successfully (no counts).`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id });
                }
                this.props.setSpinnerCount(0);
                this.loadPlacements();
                // Display multiple alerts using the AlertMessagesService.
                AlertMessagesService.showMultipleAlerts(respMsgArr);
            }
        }, (error) => {
            trackEvent(`Import failed.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, error: error?.response?.data || error });
            this.errorOnImport(error,event);
            // this.handleImportError(error,event)
        }).catch((error) => {
            this.props.setSpinnerCount(0);
            trackEvent(`Import failed (exception).`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, error });
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.importMediaPlan(event), title: 'Unable to import the placement', bodyMsgArr: []}]);
        })
    }

    handleImportError = (response,event) => {
        const resultList = response?.errors || response?.success || [];
        const alerts = resultList.map(result => {
            const type = result?.error  ? "danger" :"success" ;
            const status = result?.error  ? "Error" :"success" ;
            const title = `${MediaOpsService.getSource(status)} - Unable to import Media Plan`;
            const bodyMsgArr = [
                html`<b>Placement/Package Name</b>:<br/>${result.placement_taxonomy || result.parameter_code}`,
                ...(type !== "success" ? [html`<b>Error description</b>:<br/>${result.error}`] : []),
            ];
            return { type: type, title, bodyMsgArr };
        });

        AlertMessagesService.showMultipleAlerts(alerts);
        const errorsCount = resultList.filter(r => r?.error).length;
        const successCount = resultList.length - errorsCount;
        trackEvent(`Import results processed.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, errorsCount, successCount });
    };

    /**
     * On succes of import Function will create message body and show it
     * @param {Array} placements 
     * @param {} placementsAdded 
     * @param {} placementsUpdated 
     * @param {} isAllPlacementSuccess 
     * @returns 
     */
    OnImportSuccess = (placements,placementsAdded,placementsUpdated,isAllPlacementSuccess) => {
        let placementsFailed = 0;
        let placementErrorList = [];
        if(!isAllPlacementSuccess){
            for(let i=0;i<placements?.length;i++){
                if(placements[i].status === "error"){
                    placementsFailed++;
                    placementErrorList.push({
                        taxonomy: placements[i]["Placement Taxonomy"] ? placements[i]["Placement Taxonomy"] : placements[i]["Parameter Code"],
                        error: placements[i].validation
                    });
                }
            }
        }
        return this.createHtmlbodyMsg(placementErrorList,placementsAdded,placementsUpdated,isAllPlacementSuccess,placementsFailed);
    }

    /**
     * It will create a message in html from response
     * @param {} placementErrorList 
     * @param {} placementsAdded 
     * @param {} placementsUpdated 
     * @param {} isAllPlacementSuccess 
     * @param {} placementsFailed 
     * @returns return html message 
     */
    createHtmlbodyMsg = (placementErrorList,placementsAdded,placementsUpdated,isAllPlacementSuccess,placementsFailed) =>{
        return html`
            ${placementsAdded} placements has been added successfully.<br/>
            ${placementsUpdated} placements has been updated successfully.<br/>
            ${!isAllPlacementSuccess ? html`
            <style>
                .failedPlacementError{
                    color: var(--text-error);
                }
                .errorList {
                    border-bottom: 1px solid lightgrey;
                }
                .errorTaxonomy {
                    word-break: break-all;
                }
                .errorDesc {
                    word-break: break-all;
                    font-size: 15px;
                    font-weight: 600;
                }
            </style>
            <div class="failedPlacementError">Failed to add ${placementsFailed} placements. Please find the error details below :</div><br/>
            <ul class="menu-list">
                ${placementErrorList.map(item => html`
                    <li class="errorList">
                        <div class="errorTaxonomy">${item.taxonomy}</div>
                        <div class="errorDesc">${item.error} </div>
                    </li>
                `)}
            </ul>`: ''}`
    }

    /**
     * I will call on if import get fail, and show alret message
     * @param {object} error 
     * @param {object} event 
     */
    errorOnImport = (error,event) => {
        let respMsgArr = []
        const errorCode = error.response?.status;
        const errorType = error.response?.data?.error.type;
        const errorMessage = error.response?.data?.error.message;
        if(errorType && errorMessage){
        respMsgArr.push({type:"danger", retryBtnText: 'Retry', retryBtnEvent: () => this.importMediaPlan(event), title:`${errorCode} - Unable to import Media Plan`,"bodyMsgArr":[html`Error type: ${errorType}<br/>Error message: ${errorMessage}`]})
        }else if(errorMessage){
        respMsgArr.push({type:"danger", retryBtnText: 'Retry', retryBtnEvent: () => this.importMediaPlan(event), title:`${errorCode} - Unable to import Media Plan`,"bodyMsgArr":[html`Error message: ${errorMessage}`]})
        }else{
        respMsgArr.push({type:"danger", retryBtnText: 'Retry', retryBtnEvent: () => this.importMediaPlan(event), title:`${errorCode} - Unable to import Media Plan`,"bodyMsgArr":['Error message: Placements are not Imported!']})
        }
        this.props.setSpinnerCount(0);
        AlertMessagesService.showMultipleAlerts(respMsgArr);
        trackEvent(`Import failed (errorOnImport).`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, errorCode, errorType, errorMessage });
    }

    /**
     * Check the status of placemetn, if it's for syn error then sending true or false
     * @param {object} value placement detail
     * @returns return boolen to for condition.
     */
    checkStatusType(value){
        let isSyncModalNeed = false;
        let syncStatusList = ['sync id error', 'sync error', 'in platform'];
        if(value?.mediaocean_status){
            isSyncModalNeed = syncStatusList.some(item => value?.mediaocean_status.toLowerCase()?.includes(item));
        }
        return isSyncModalNeed;
    }
    
    //  open sync error modal 
     
    /**
     * Opens the placement modal.
     * @returns {void}
     */
    onSyncErrorModalOpen = (value) => {

        let data = value?.error_description?.split?.('MO Data : ')?.[1];
        //if error description is not there not opening the modal
        if(!data){
            AlertMessagesService.showErrorToastMessage("No Mediaocean data found to display.");
            return;
        }
        let jsonStringArr = data ? data.split(', Parameter Code : '): [];
        data = jsonStringArr?.length ? jsonStringArr[0] : '';

        data = Utils.sanitizeStringToValidJson(data);
        /**
         * The below code is defining a function called `convertDates` which takes in four parameters:
         * `match`, `year`, `month`, and `day`. The function then creates a new date object and sets
         * the year, month, and day of the date object to the values of the `year`, `month`, and `day`
         * parameters. The function then splits the `match` parameter by the colon character and
         * retrieves the first element of the resulting array. The function then returns a string
         * containing the `matchKey` and the ISO string of the date object.
         */
        function convertDates(match, year, month, day) { 
            const date = new Date(); 
            date.setFullYear(year, month - 1, day); 
            const matchKey = match.split(':')[0];
            return `${matchKey}: "${date.toISOString()}"`;
        }

        data = data.replace(/"flightStart": datetime.date\(([^,]+),\s*(\d+),\s*(\d+)\)/g, convertDates); 
        data = data.replace(/"flightEnd": datetime.date\(([^,]+),\s*(\d+),\s*(\d+)\)/g, convertDates);
        
        try {
            data = JSON.parse(data);
        } catch (error) {
            console.error("Error parsing JSON: Error from console", error);
            AlertMessagesService.showErrorToastMessage("Unable to process Mediaocean data. Invalid format received.");
            data = {};
        }
        
        //moh value belwo in object to compare value in table
        data = {...data, 
            plannedCost: data.placementCost?.plannedCost?.amount, 
            unitAmount: data.placementCost?.unitAmount || data.placementCost?.unitAmount?.amount, 
            rate: data.placementCost?.rate?.amount, 
            flightStart: data.flightStart?.split('T')[0], 
            flightEnd: data.flightEnd?.split('T')[0],
            parameter_code : jsonStringArr?.length > 1 ? jsonStringArr[1]:'',
            placement_taxonomy : data?.placementName,
            mohPlannedCost: value?.planned_cost, 
            mohUnitAmount: value?.unit_amount, 
            mohRate: value?.rate, 
            mohFlightStart: value.flight_start,
            mohflightEnd: value.flight_end,
            mohParameter_code : value?.parameter_code,
            mohplacement_taxonomy : value?.placement_taxonomy,
            parent_type : value?.parent_type,
            package_type:value?.package_type, 
            placement_type:value?.placement_type
        }

        //add mo detail , just to compaire value in table
        let compareValue = structuredClone(value);
        delete compareValue['nestedData'];

        value = {...compareValue,
            flight_start:compareValue?.flight_start,
            flight_end:compareValue?.flight_end,
            moPlannedCost: data.placementCost?.plannedCost?.amount, 
            moUnitAmount: data.placementCost?.unitAmount || data.placementCost?.unitAmount?.amount, 
            moRate: data.placementCost?.rate?.amount, 
            moFlightStart: data.flightStart?.split('T')[0], 
            moflightEnd: data.flightEnd?.split('T')[0],
            moParameter_code : jsonStringArr?.length > 1 ? jsonStringArr[1]:'',
            moplacement_taxonomy : data?.placementName
        }

        let placemementSyncData = PlacementUtils.getPlacementTaxonomyValuesAndNames(this.state,this.placementTaxonomyMap, this.allTaxonomyData,value,data,true);
        data.placementName = placemementSyncData.secondName;
        value.placementName = placemementSyncData.firstName;

        let parameterSyncData = PlacementUtils.getPlacementTaxonomyValuesAndNames(this.state,this.placementTaxonomyMap, this.allTaxonomyData,value,data,false);
        data.parameterCode = parameterSyncData.secondName;
        value.parameterCode = parameterSyncData.firstName;
        
        const syncErrorModalData = [value, data]
        const columnsWithErrors = compareValue?.error_description?.substring(0, compareValue?.error_description?.indexOf(", MO Data"))
        this.setState({ syncErrorModalData, isSyncErrorModalOpen: true, syncErrorDescription: columnsWithErrors,statusType:compareValue.mediaocean_status });
    }

    onSyncErrorModalClose = (isSendToMediaOcean) => {
        this.setState({ isSyncErrorModalOpen: false, syncErrorDescription: '',statusType:'' });
        //getting true if placement sent to mo in sync modal
        if(isSendToMediaOcean){
            this.loadPlacements();
        }
        trackEvent(`Sync Error modal closed.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, isSendToMediaOcean: !!isSendToMediaOcean });
    }

    /**
     * Opens the platform modal with the appropriate data based on the value's mediaocean status and package type.
     * If the mediaocean status is 'Not Exported', the function returns early.
     * If the package type is 'Delivered', the modal data is set to the value itself.
     * Otherwise, the modal data is set to the first delivered taxonomy of the value.
     *
     * @param {Object} value - The value object containing mediaocean status, package type, and delivered taxonomies.
     */
    openPlatformModal = (value) => {        
        if (value?.package_type === "Delivered") {
            trackEvent(`Platform detail modal opened (Delivered).`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, placementId: value?.id });
            this.setState({ plateformModalData: [value], isPlatformModalOpen:true });
          } else {
            // setting the platform modal data
            let platformData = [];
            for(let i=0;i<value?.delivered_taxonomies.length;i++){
                platformData.push({
                    placement_taxonomy : value?.delivered_taxonomies[i]?.taxonomy_value,
                    package_type: value?.delivered_taxonomies[i]?.taxonomy_name,
                    status: value?.delivered_taxonomies[i]?.status
                })
            }
            trackEvent(`Platform detail modal opened.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, count: value?.delivered_taxonomies?.length });
            this.setState({ plateformModalData: platformData, isPlatformModalOpen:true });
          }
    }

    /**
     * Handle the move placement action by opening the move placement modal
     * @param {string|number} placementId - The ID of the placement to be moved
     */
    movePlacement = (placementId) => {
        const rowNode = this.gridRef?.current?.api?.getRowNode(placementId);
        const placement = rowNode?.data;

        if (!placement) {
            console.error("Placement not found for ID:", placementId);
            return;
        }

        // Check if placement is already sent to the buying system
        if (['placement sent'].includes(placement?.mediaocean_status?.toLowerCase())) {
            AlertMessagesService.showErrorToastMessage("Cannot move placement, as placement is already sent to buying system");
            trackEvent(`Move placement blocked (already sent).`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, placementId });
            return;
        }

        // Get available packages for the placement
        const availablePackages = this.getAvailablePackages(placement);
        
        // Set the placement as a class property instead of state
        this.selectedPlacementForMove = placement;
        
        // Update state without selectedPlacementForMove
        this.setState({
            movePlacementModalOpen: true,
            availablePackagesForMove: availablePackages,
            selectedPackageId: ''
        });
        trackEvent(`Move placement modal opened.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, placementId, availablePackages: availablePackages?.length });
    };
    
    /**
     * Handle package selection in the move placement modal
     * @param {string} packageId - The ID of the selected package
     */
    handlePackageSelection = (packageId) => {
        this.setState({ selectedPackageId: packageId });
    };

    closeMovePlacementModal = () => {
        // Clear the class property
        this.selectedPlacementForMove = null;
        
        this.setState({
            movePlacementModalOpen: false,
            availablePackagesForMove: [],
            selectedPackageId: ''
        });
    };

    handleMovePlacement = () => {
        // Use class property instead of state
        const { selectedPackageId } = this.state;
        
        if (!this.selectedPlacementForMove || !selectedPackageId) {
            return;
        }

        this.props.setSpinnerCount(1);
        trackEvent(`Move placement initiated.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, placementId: this.selectedPlacementForMove?.id, targetPackageId: selectedPackageId });
        
        const payload = {
            "clientId": Utils.getRouteParam("clientid"),
            "parentId": selectedPackageId,
            "packageType": "Child",
            "updated_by": this.props?.omniProfile?.email
        };
        
        const campaignId = encodeURIComponent(this.state.campaignObj.id);
        
        MediaOpsService.movePlacement(campaignId, this.selectedPlacementForMove.id, payload, this.props?.versionOneApi)
            .then(result => {
                AlertMessagesService.showSuccessToastMessage('You have successfully moved the placement.');
                this.loadPlacements();
                trackEvent(`Move placement succeeded.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id });
            })
            .catch(error => {
                AlertMessagesService.showMultipleAlerts([{
                    type: 'danger', 
                    retryBtnText: 'Retry', 
                    retryBtnEvent: () => this.handleMovePlacement(), 
                    title: 'Move Placement API cannot be loaded', 
                    bodyMsgArr: []
                }]);
                this.props.setSpinnerCount(0);
                trackEvent(`Move placement failed.`, { access_from: 'Placements Page', campaignId: this.state?.campaignObj?.id, error: error?.response?.data || error });
            })
            .finally(() => {
                this.closeMovePlacementModal();
            });
    };

    /**
     * Get available packages for a placement
     * @param {object} placement - The placement object
     * @returns {array} - Array of package objects with id and placement_taxonomy
     */
    getAvailablePackages = (placement) => {
        if (!this.gridRef?.current?.api) {
            return [];
        }
        
        const packageValues = [];
        
        this.gridRef.current.api.forEachNode(node => {
            const data = node.data;
            
            switch (true) {
                // Skip if no data or it's a special type
                case !data || ['Channel', 'Publisher'].includes(data.title):
                    return;
                
                // Check if it's not a package type
                case !data.package_type?.includes("Package"):
                    return;
                
                // Can't move to the current parent
                case placement.parent_id === data.id.toString():
                    return;
                
                // Can't move to packages sent to Media Ocean
                case data.mediaocean_status === "Placement Sent":
                    return;
                
                // Can't move to packages with accepted export status
                case ["accepted"].includes(data?.export_status?.toLowerCase()):
                    return;
                
                // This package is eligible
                default:
                    packageValues.push({
                        id: data.id,
                        placement_taxonomy: data.placement_taxonomy
                    });
                    break;
            }
        });
        
        return packageValues;
    };

    /**
     * function is use to check if ad server fee and ad verification fee is avilable
     * if not then we use value to hide ad server and ad verification in create and edit placement
     */
    getAdServeingAndAdVerifcationRateAndFee = ()=>{
        const adServer = RatesAndFeesService.getRatesAndFees('Ad Server');
        const adVerification = RatesAndFeesService.getRatesAndFees('Ad Verification');
        Promise.all([adServer,adVerification]).then(response=> {
            if(response[0]?.length){
                this.rateAndFees.adServer = true
            }
            if(response[1].length){
                this.rateAndFees.adVerification = true
            }
            
        }).catch(error=>{
            console.error(error)
        })
    }

    onGridReady = (params) => { };

    render() {
        // Set up the context for AG-Grid components
        const gridContext = {
            componentParent: this // Remove onTaxonomyPartClick from context
        };

        return (
            <div className='placement-table-container ag-theme-alpine' style={{padding: "20px" }}>

                {this.state.loading ? (
                    <div style={{top: '0%', position: 'absolute'}}><LoaderComponent spinnerCount={1} displayWholePage={true} /></div>
                ) : (
                    <>
                        <PlacementDetailsTableSummaryHeader data={this.state.placementsFromApi} campaignObj={this.state.campaignObj} currencyName={this.state.campaignObj.local_currency_name} isLocal={this.props.systemParametersData.data.isLocal}
                        ></PlacementDetailsTableSummaryHeader>

                        <OmniToolbarComponent slot='header'>
                            <div slot='start' className='archive-taps'>
                                <button className={`${this.state.archiveTab ? 'tertiary' : ''}`} onClick={() => this.setActiveTab(false)}>Active placements</button>
                                <button className={`${this.state.archiveTab ? '' : 'tertiary'}`} onClick={() => this.setActiveTab(true)}>Archived placements</button>
                            </div>

                            {!this.state.archiveTab &&
                                <div slot="end" className="custom-dropdown-menu create-button-container">
                                    <ul className="menus">
                                        {this.channelDropdownList.map((menu, index) => {
                                            const depthLevel = 0;
                                            return (
                                                <React.Fragment key={index}>
                                                    {(this.state.firstChildCount > 1 || this.state.secondChildCount > 1) ?
                                                        <OmniTooltipNextComponent>
                                                            <button slot="invoker" className="button" disabled>Create</button>
                                                            <div slot="content">Only one Channel and Publisher should be selected</div>
                                                        </OmniTooltipNextComponent> :
                                                        <MenuItems items={menu} key={index} depthLevel={depthLevel} createProgrammaticTypes={this.createProgrammaticTypes} />
                                                    }
                                                </React.Fragment>
                                            )
                                        })}
                                    </ul>
                                </div>
                            }
                            <div slot="end">
                                <button className="secondary" onClick={() => this.openCreative()}>
                                    Manage Creative
                                </button>
                            </div>    
                            {!this.state.archiveTab &&
                                <>
                                    <div slot="end" className="buying-system-dropdown">
                                        {(this.props.systemParametersData?.isLoaded && !this.props.systemParametersData?.data?.isApprovalRequired)
                                            ? <button disabled={!this.state.selectedItems.length || !Utils.checkForClientCodeAndBuyingSystem(this.props?.systemParametersData, this.state?.campaignObj?.client_code)} className="buying-system-button secondary" onClick={() => this.handleBuyingSystemChange("Send to Buying System")}>Send to Buying System</button>
                                            :
                                            <>
                                                <button className="button medium buying-system-button secondary" disabled={!this.state.selectedItems.length || (this.state.selectedItems.length && this.state.selectedItems.some(item => item.data_source?.toLowerCase() === 'planit'))}
                                                    onClick={() => {
                                                        const dropdownContainer = document.querySelector('.buying-system-dropdown');
                                                        const dropdownMenu = document.querySelector('.buying-system-dropdown-menu');
                                                        if (dropdownContainer) {
                                                            dropdownContainer.classList.add('dropdown', 'is-active');
                                                            dropdownMenu.focus();
                                                            dropdownMenu.addEventListener('blur', () => {
                                                                if (!dropdownContainer.contains(document.activeElement)) {
                                                                    dropdownContainer.classList.remove('is-active');
                                                                }
                                                            })
                                                        }
                                                    }}
                                                >
                                                    Buying System Options</button>
                                                <div className="dropdown-menu buying-system-dropdown-menu" id="dropdown-menu" role="menu" tabIndex={0}>
                                                    <div className="dropdown-content">
                                                        <button className="dropdown-item tab" 
                                                            onMouseDown={(event) => {
                                                                event.preventDefault();
                                                                this.handleBuyingSystemChange("Approval for Buying System");
                                                                document.querySelector('.buying-system-dropdown').classList.remove('is-active');
                                                            }}
                                                        >Approval for Buying System</button>
                                                        <button className="dropdown-item tab"
                                                            disabled={!Utils.checkForClientCodeAndBuyingSystem(this.props?.systemParametersData, this.state?.campaignObj?.client_code)}
                                                            onMouseDown={(event) => {
                                                                event.preventDefault();
                                                                this.handleBuyingSystemChange("Send to Buying System");
                                                                document.querySelector('.buying-system-dropdown').classList.remove('is-active');
                                                            }}
                                                        >Send to Buying System</button>
                                                    </div>
                                                </div>
                                            </>
                                        }
                                    </div>
                                    {(!this.state.selectedItems.length || this.state.selectedItems.length ) &&
                                        <>
                                            <div slot="end" className="custom-dropdown-menu">
                                                <OmniIconMenu
                                                    options={this.props.checkPermissions(this.props.omniProfile?.project?.userRoles,'enableTemplateEdit') ? this.importExportOptions : this.importExportOptions.filter(option => option !== 'Edit template')}
                                                    iconId="omni:interactive:importExport"
                                                    iconClass="is-size-4"
                                                    tooltip="Import/Export"
                                                    onDropdownChange={(event) => this.importExportClick(event)}>
                                                </OmniIconMenu>    
                                            </div>
                                            <div slot='end'>
                                                <OmniTooltipNextComponent>
                                                    <button disabled={!this.state.selectedItems.length} slot="invoker" aria-haspopup="true" className="button is-text is-shadowless" onClick={(event) => this.handleBulkCopy(event)}>
                                                        <OmniIconComponent className="is-size-4 icon-in-header" icon-id="omni:interactive:copy"></OmniIconComponent>
                                                    </button>
                                                    <div slot="content">Copy</div>
                                                </OmniTooltipNextComponent>
                                            </div>
                                        </>
                                    }
                                </>
                            }
                            
                            <div slot='end'>
                                <OmniTooltipNextComponent>
                                    <button disabled={!this.state.selectedItems.length} slot="invoker" aria-haspopup="true" className="button is-text is-shadowless" onClick={() => this.confirmArchiveMsg(true)}>
                                        <OmniIconComponent className="is-size-4 icon-in-header" icon-id={this.state.archiveTab ? "omni:interactive:unarchive" : "omni:interactive:archive"}></OmniIconComponent>
                                    </button>
                                    <div slot="content">{this.state.archiveTab ? 'Restore archived' : 'Archive'}</div>
                                </OmniTooltipNextComponent>
                            </div>
                        </OmniToolbarComponent>

                        <div style={{ height: 'calc(100vh - 320px)' }}   ref={(el) => (this.gridContainerRef = el)}>
                            <AgGridReact
                                key={this.state?.placementsWithNestedStructure.length}
                                ref={this.gridRef}
                                columnDefs={this.state.columnDefs}
                                rowData={this.state.placementsWithNestedStructure}
                                treeData={true}
                                treeDataChildrenField={"nestedData"}
                                getRowId={(params) => params?.data?.id?.toString()}
                                rowSelection={{
                                    mode: "multiRow",
                                    groupSelects: "descendants",
                                    checkboxLocation: 'autoGroupColumn',
                                    hideDisabledCheckboxes: true,
                                    isRowSelectable: (node) => {
                                        if(this.state.selectedItems?.length && node?.data){
                                            const firstSelectedIsPlanit = this.state.selectedItems[0].data_source?.toLowerCase() === "planit";
                                            const nodeIsPlanit = node?.data?.data_source?.toLowerCase() === "planit";
                                            return firstSelectedIsPlanit === nodeIsPlanit;
                                        }
                                        return true;
                                    }
                                }}
                                onCellValueChanged={this.handleCellValueChanged}
                                onCellClicked={this.onCellClicked}
                                onSelectionChanged={this.onSelectionChanged}
                                defaultColDef={defaultColDef}
                                sideBar={true}
                                statusBar={this.state.statusBar}
                                context={gridContext}
                                rowClassRules={{
                                    'highlight-row': (params) =>  params.data?.highlight || (this.props?.state?.navigateFromGlobalSearch && this.props?.state?.placementId === params.data?.placements_id), // Add class if condition is met
                                }}
                                domLayout="normal"
                                components={{
                                    dropdownMenuCellRenderer: DropdownMenuCellRenderer,
                                }}
                                // getContextMenuItems={contextMenuItems}
                                onGridReady={this.onGridReady}
                                groupDefaultExpanded={-1}
                                groupDisplayType={"custom"}
                                cellSelection={{
                                    handle: { 
                                        mode: "fill",
                                        direction: 'y',
                                     }
                                }}
                                onFillEnd={this.onFillEnd}
                                // copyHeadersToClipboard ={true}
                                ensureDomOrder ={true}
                                tooltipShowDelay={0}
                                rowBuffer={10}
                            />
                        </div>

                        <div className='footer-container'>
                            <PlacementDetailsTableSummaryHeader data={this.state.placementsFromApi} campaignObj={this.state.campaignObj} currencyName={this.state.campaignObj.local_currency_name} isLocal={this.props.systemParametersData.data.isLocal}
                            ></PlacementDetailsTableSummaryHeader>
                        </div>

                        {/* modal dialog */}
                        {this.state?.campaignObj && this.state?.isPlacementModalOpen && <PlacementsDialogComponent
                            allTaxonomyData={this.allTaxonomyData}
                            campaignTaxonomyData={this.props.campaignTaxonomyData}
                            placementCategory={this.state.placementCategory}
                            onDialogClose={this.close}
                            systemParametersData={this.props?.systemParametersData}
                            omniProfile={this.props.omniProfile}
                            editDailog={this.state.editDailog}
                            placementData={this.state.placementData}
                            vendorMappingsData={this.vendorMappingMap}
                            campaignObj={this.state.campaignObj}
                            placementTaxonomyMap={this.placementTaxonomyMap}
                            versionOneApi={this.props?.versionOneApi}
                            isPlacementModalOpen={this.state?.isPlacementModalOpen}
                            selectedItems={this.getDialogSelectedItems()}
                            onPlacementCreate={this.onPlacementCreate}
                            CallLineItemProgramatically={this.CallLineItemProgramatically}
                            placementNestedData = {this.state.placementsWithNestedStructure}
                            rateAndFees = {this.rateAndFees}
                            setSpinnerCount={this.props.setSpinnerCount}
                            computeSmartSuggestions={this.computeSmartSuggestionsLazy}
                        />}

                        {/* Inline Edit Dialog */}
                        {this.state.displayInlineEditModal && this.placementRowData && <InlineEditDialogBox
                            selectedRowData={this.placementRowData}
                            displayInlineEditModal ={this.state.displayInlineEditModal}
                            onInlineModalClose={this.close}
                            isOverride = { this.isInlineOverride }
                            taxonomyMap ={this.placementTaxonomyMap} 
                            allTaxonomyData = {this.allTaxonomyData}
                            systemParametersData = {this.props.systemParametersData}
                            campaignObj = {this.state.campaignObj}
                            omniProfile = {this.props.omniProfile}
                            versionOneApi = {this.props?.versionOneApi}
                            editFormData = {this.state?.editFormData}
                            taxonomySchemaArray = {this.state?.taxonomySchemaArray}
                            setSpinnerCount={this.props.setSpinnerCount}                         
                            taxonomyKeys={this.state.taxonomyKeys}                         
                        /> } 
                        <SyncErrorDialogComponent
                        isSyncErrorModalOpen={this.state.isSyncErrorModalOpen}
                        onSyncErrorModalClose={this.onSyncErrorModalClose}
                        syncErrorModalData={this.state.syncErrorModalData}
                        omniProfile={this.props.omniProfile}
                        parentProps={this.props}
                        onPlacementCreate={this.onPlacementCreate}
                        campaignObj={this.state?.campaignObj}
                        syncErrorDescription={this.state.syncErrorDescription}
                        versionOneApi = {this.props?.versionOneApi}
                        statusType = {this.state.statusType} 
                        setSpinnerCount = {this.props.setSpinnerCount} 
                        />
                        
                        <PlatformDetailDialogComponent
                        onDialogClose={this.close}
                        plateformModalData={this.state.plateformModalData}
                        placementNestedData={this.state.placementNestedData}
                        //channelDropdownData={this.state.channelDropdownData}
                        isPlatformModalOpen = {this.state.isPlatformModalOpen}
                        />
                        
                        {/* Move Placement Modal */}
                        <MovePlacementForm 
                            isOpen={this.state.movePlacementModalOpen}
                            onClose={this.closeMovePlacementModal}
                            packages={this.state.availablePackagesForMove}
                            selectedPackageId={this.state.selectedPackageId}
                            onPackageSelect={this.handlePackageSelection}
                            onMove={this.handleMovePlacement}
                            disabled={!this.state.selectedPackageId}
                        />

                        {/* Edit Template Modal */}
                        <EditTemplateDialog 
                            isOpen={this.state.editTemplateModalOpen}
                            onClose={this.closeEditTemplateModal}
                        />

                        {/* Supplier Budget Warning Modal */}
                        <OverBudgetWarningModal
                            isOpen={this.state.showSupplierBudgetWarning}
                            onClose={this.closeSupplierBudgetWarning}
                            onContinue={this.proceedWithBudgetOverride}
                            overBudgetItems={this.state.overBudgetSuppliers}
                            localCurrency={this.state.campaignObj.local_currency_name}
                            isLocal={this.props?.systemParametersData?.data?.isLocal}
                            warningMessage="You're about to send placements to Buying System that exceed the planned media budget in Planit."
                        />

                        {/* Campaign Status Warning Modal */}
                        <CampaignStatusWarningModal
                            isOpen={this.state.showCampaignStatusWarning}
                            onClose={this.closeCampaignStatusWarning}
                            onSendCampaignFirst={this.proceedSendCampaignThenPlacements}
                            onSendPlacementsOnly={this.proceedSendPlacementsOnly}
                        />
                    </>
                )}
                <input type="file" id="fileInput" accept=".xlsx, .xls" style={{display: 'none'}} />
            </div>
        );
    }
}

export default PlacementTableAgGrid;